import React, {
  useState,
  useEffect,
  useRef,
  createRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { makeStyles } from '@material-ui/styles';
import { useStore, useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Grid,
  Box,
  Snackbar,
  IconButton,
  Toolbar,
  Typography,
  Collapse
} from '@material-ui/core';
import TemplateKeyBank from 'components/Template/TemplateKeyBank';
import UploadsPanel from 'components/Template/UploadsPanel';
import get from 'lodash.get';
import { Code } from '@material-ui/icons';
import Alert from 'components/Alert';
import sessionHelper from 'helpers/sessionHelper';
import filterDataFrame from './filterDataFrame';
import formSurveyKeys from './formSurveyKeys';
import { getEnvVar } from 'helpers';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/grapes.min.js';
import 'grapesjs/dist/css/grapes.min.css';
import 'grapesjs-preset-webpage/dist/grapesjs-preset-webpage.min.js';
import 'grapesjs-preset-webpage/dist/grapesjs-preset-webpage.min.css';
import { exportGrapeJsContent, setGrapesJsBodyId } from 'store/helpers/helperFunctions';
import { isReadOnlyMode, isArrayValuesEqual, isArrayLengthEqual, sanitizeHtml } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../../../views/Settings/useLanguageSettings';
import { Base64 } from 'js-base64';
import { actions as uploadActions } from 'store/uploads';
import axios from 'axios';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  iconButtonLabel: {
    display: 'flex',
    flexDirection: 'column'
  },
  editorWrapper: {
    position: 'relative',
    paddingTop: '0px !important'
  },
  templateKeysPanel: {
    backgroundColor: 'rgb(249, 249, 249)',
    height: '900px'
  },
  toolContainer: {
    position: 'relative',
    right: 0,
    zIndex: '10',
    float: 'right',
    top: -6,
    height: '507px',
    paddingTop: '6px'
  },
  tools: {
    padding: '0px'
  },
  subjectLine: {
    flex: '1 1 0%',
    paddingRight: theme.spacing(4)
  },
  editorBar: {
    margin: '0px 10px'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-around'
  }
}));

const TOOLBAR_SMALL_WIDTH = 353;
const TOOLBAR_LARGE_WIDTH = 155;

const EDITOR_WIDTH_BREAKPOINT_LARGE = 1170;
const EDITOR_WIDTH_BREAKPOINT_SMALL = 1031;
const TEMPLATE_EDITOR_WIDTH = 661;
const DEFAULT_IMAGE_PLACEHOLDER_URL = 'https://cdn.tools.unlayer.com/image/placeholder.png';

const acceptTypes = (getEnvVar(`ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS`) || `image/jpeg,image/png,image/jpg`).split(',');
const acceptTypesLabel = getEnvVar(`ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS_LABEL`) || `JPEG, PNG and JPG`;
const acceptLimit = Number(getEnvVar(`ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB`) || 10) * 1024 * 1024;
const acceptLimitLabel = getEnvVar(`ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB`) || `10`;
const imagePlaceholder = getEnvVar(`ADM_IMAGE_PLACEHOLDER`) || `https://cdn.tools.unlayer.com/image/placeholder.png`;

const getAssetImageLocation = asset =>
  String(asset?.cdnPublicUrl || asset?.publicUrl || '').trim();

const getContentServiceUploadApiRoot = (tenantId, businessContextId) =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/content-service/content/adm/v1/tenants/${tenantId}/properties/${businessContextId}/azure`;

const TemplateEditor = forwardRef((props, ref) => {
  const {
    thisTemplate,
    surveys,
    useUnsubscribeLinkOption,
    handleChange,
    onFocus,
    onBlur,
    saveTemplate,
    children,
    shouldAutoSave,
    autoSaveTemplate,
    setIsValidationError,
    isLoading
  } = props;

  const hasNestedDataFrame = (props?.templateKeys?.flatMap(schema =>
    schema.schemaConditionAttributes?.filter(attr =>
      attr.hasNestedDataFrame && !attr.metaAttribute
    ) || []
  ) || []).length > 0;

  const templateKeys = () => {
    let tempKeys = props.templateKeys;
    tempKeys = tempKeys.map(keys => {
      let schemaConditionAttributes =
        keys.schemaConditionAttributes &&
        keys.schemaConditionAttributes.filter(
          i => i.attributeFieldType !== 'dataFrame'
        );
      return { ...keys, schemaConditionAttributes: schemaConditionAttributes.sort((a, b) => a?.name.localeCompare(b?.name)) };
    });
    return tempKeys;
  };

  const classes = useStyles();
  const store = useStore();
  const { refreshSessionIfNeeded } = sessionHelper(store);
  const { templateLanguageContent, getTemplateLanguageContent } = useLanguageSettings();
  const [editorStaticContent, setEditorStaticContent] = useState(null);

  const currentTemplateLanguage = useSelector(store => store.settings.currentTemplateLanguage);
  const uploadAssets = useSelector(store => store.uploads?.list || []);
  const [grapesLocaleContent, setGrapesLocaleContent] = useState(null);
  const [triggerSave, setTriggerSave] = useState(false);
  const [toolBarWidth, setToolBarWidth] = useState(TOOLBAR_LARGE_WIDTH);
  const [windowWidth, setWindowWidth] = useState(null);
  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  const [keysOpen, setKeysOpen] = useState(false);
  const [uploadsOpen, setUploadsOpen] = useState(false);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const dispatch = useDispatch();

  const returnFileSize = number => {
    if (number < 1024) {
      return `${number} bytes`;
    } else if (number >= 1024 && number < 1048576) {
      return `${(number / 1024).toFixed(1)} KB`;
    } else if (number >= 1048576) {
      return `${(number / 1048576).toFixed(1)} MB`;
    }
  };

  const pendingSavedContentRef = useRef(null);
  const saveHydrationGuardTimerRef = useRef(null);
  const saveHydrationGuardUntilRef = useRef(0);
  const uploadAssetsRef = useRef(uploadAssets);
  const activeImageComponentRef = useRef(null);

  useEffect(() => {
    uploadAssetsRef.current = uploadAssets;
  }, [uploadAssets]);
  const [dataFrameKeys, setDataFrameKeys] = useState(
    filterDataFrame(props.templateKeys, thisTemplate)
  );

  const emailEditorRef = createRef();
  const emailWrapperRef = createRef({ current: {} });
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    dispatch({ type: uploadActions.GET_UPLOADS });
  }, [dispatch]);

  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );
  const settingsMetaDataStore = useSelector(
    store => store.settings.settingsMetaData.data
  );

  const applySelectedAssetImage = imageUrl => {
    if (!imageUrl || !editor) {
      return;
    }

    const currentSelection = editor.getSelected();
    const selectedComponent =
      currentSelection?.get('type') === 'image'
        ? currentSelection
        : activeImageComponentRef.current;

    if (!selectedComponent || selectedComponent.get('type') !== 'image') {
      openSnackBarError('warning', `${editorStaticContent?.select_an_image || 'Select an image component before choosing an upload.'}`);
      return;
    }

    activeImageComponentRef.current = selectedComponent;

    // Check if it's a newly added image component and reset its default 100% width on customization
    const isNew = selectedComponent.get('isNewImageComponent');
    if (isNew) {
      selectedComponent.addStyle({
        height: 'auto',
        maxHeight: 'none',
        width: 'auto'
      });
      selectedComponent.set('isNewImageComponent', false);
    } else {
      selectedComponent.addStyle({
        height: 'auto',
        maxHeight: 'none'
      });
    }

    selectedComponent.set('src', imageUrl);
    selectedComponent.set('imageSourceUrl', imageUrl);
    selectedComponent.addAttributes({
      src: imageUrl,
      'data-image-source': 'url',
      'data-image-source-url': imageUrl
    });
    const imageEl = selectedComponent.getEl?.();
    if (imageEl) {
      imageEl.setAttribute('src', imageUrl);
      imageEl.src = imageUrl;
    }
    selectedComponent.trigger('change:src');
    selectedComponent.trigger('change:attributes');
    selectedComponent.view?.render?.();
    editor.select(selectedComponent);
    editor.trigger('component:selected', selectedComponent);

    // Keep the style manager URL field and emit pipeline in sync.
    const imageUrlInput = document.querySelector(
      '.gjs-sm-property[data-property="image-source"] .gjs-image-url-input'
    );
    if (imageUrlInput) {
      imageUrlInput.value = imageUrl;
      imageUrlInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setUploadsOpen(false);
  };

  const handleUploadsPanelFiles = async files => {
    if (!files?.length) {
      return;
    }
    if (!currentTenantId || !currentBusinessContextId) {
      openSnackBarError('error', `${editorStaticContent?.missing_tenant_business_context || 'Unable to upload: missing tenant or business context.'}`);
      return;
    }

    setIsUploadingAssets(true);
    let uploadedCount = 0;
    try {
      const endpoint = getContentServiceUploadApiRoot(currentTenantId, currentBusinessContextId);
      const customTag = getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm';
      for (const file of files) {
        if (acceptTypes.indexOf(file.type) < 0) {
          openSnackBarError(
            'error',
            `${file?.name} ${editorStaticContent?.dataframe_error_image_size[0] || `is not supported`}. ${acceptTypesLabel}`
          );
          continue;
        }
        if (file.size > acceptLimit) {
          openSnackBarError(
            'error',
            `${editorStaticContent?.dataframe_error_image_format[0] || `Size of`} ${file?.name} ${editorStaticContent?.dataframe_error_image_format[1] || `is`} ${returnFileSize(file?.size)}. ${editorStaticContent?.dataframe_error_image_format[2] || `Maximum allowed size is`} ${acceptLimitLabel} MB`
          );
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(endpoint, formData, {
          params: {
            customTag: customTag
          }
        });
        uploadedCount++;
      }
      dispatch({ type: uploadActions.GET_UPLOADS });
      if (uploadedCount > 0) {
        openSnackBarError('success', `${editorStaticContent?.image_upload_success || 'Image upload successful.'}`);
      }
    } catch (error) {
      const responseData = error?.response?.data;
      const isDuplicate = error?.response?.status === 409 || responseData?.status === 409 || responseData?.code === 4002;
      const message = isDuplicate
        ? `${editorStaticContent?.image_duplicate_error || 'Image already exists. Try with a different name.'}`
        : (responseData?.message || 'Failed to upload image.');
      openSnackBarError('error', message);
    } finally {
      setIsUploadingAssets(false);
    }
  };

  const handleDeleteUpload = async asset => {
    const uploadId = asset?.id || asset?.assetId;
    if (!uploadId) return;
    const deleteEndpoint = `${getContentServiceUploadApiRoot(currentTenantId, currentBusinessContextId)}/${encodeURIComponent(String(uploadId))}`;
    try {
      await axios.delete(deleteEndpoint);
      dispatch({ type: uploadActions.GET_UPLOADS });
      openSnackBarError('success', editorStaticContent?.deleted_image_success || 'Image deleted successfully.');
    } catch (error) {
      openSnackBarError('error', error?.response?.data?.message || editorStaticContent?.deleted_image_error || 'Failed to delete image.');
    }
  };

  const dateFormatMenuOptions = [
    { value: 'MM/DD/YYYY', displayExample: '04/19/2021' },
    { value: 'MM/DD/YY', displayExample: '04/19/21' },
    { value: 'MM/DD', displayExample: '04/19' },

    { value: 'DD/MM/YYYY', displayExample: '19/04/2021' },
    { value: 'DD/MM/YY', displayExample: '19/04/21' },
    { value: 'DD/MM', displayExample: '19/04' },

    { value: 'dddd, MMMM DD, YYYY', displayExample: `${editorStaticContent?.mondayLabel || `Monday`}, ${editorStaticContent?.aprilLabel || `April`} 21, 2021` },
    { value: 'dddd, DD MMMM, YYYY', displayExample: `${editorStaticContent?.mondayLabel || `Monday`}, 21 ${editorStaticContent?.aprilLabel || `April`}, 2021` },

    { value: 'MMMM DD', displayExample: `${editorStaticContent?.aprilLabel || `April`} 21` },
    { value: 'MMMM DD, YYYY', displayExample: `${editorStaticContent?.aprilLabel || `April`} 21, 2021` },
    { value: 'DD MMMM, YYYY', displayExample: `21 ${editorStaticContent?.aprilLabel || `April`}, 2021` },
    { 
      value: 'MMM DD, YYYY hh:mm a',
      displayExample: `${editorStaticContent?.janLabel || `Jan`} 17, 2023 01:30 PM`
    },
    {
      value: 'MMM DD, YYYY hh:mm a',
      displayExample: `${editorStaticContent?.janLabel || `Jan`} 17, 2023 01:30 PM (${editorStaticContent?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    },
    {
      value: 'dddd, MMM DD, YYYY hh:mm a',
      displayExample: `${editorStaticContent?.mondayLabel || `Monday`}, ${editorStaticContent?.janLabel || `Jan`} 17, 2023 01:30 PM`
    },
    {
      value: 'dddd, MMM DD, YYYY hh:mm a',
      displayExample: `${editorStaticContent?.mondayLabel || `Monday`}, ${editorStaticContent?.janLabel || `Jan`} 17, 2023 01:30 PM (${editorStaticContent?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    },
    { value: 'hh:mm a', displayExample: '01:30 PM' },
    {
      value: 'hh:mm a',
      displayExample: `01:30 PM (${editorStaticContent?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    }
  ];

  const alignmentOptions = [
    {
      value: 'None'
    },
    {
      value: 'Left'
    },
    {
      value: 'Center'
    },
    {
      value: 'Right'
    }
  ];

  // To be used in future.
  const barcodeFormatOptions = [
    {
      value: 'CODE128'
    },
    {
      value: 'CODE39'
    }
  ];

    const borderStyleOptions = [
    {
      "id": "none"
    },
    {
      "id": "solid"
    },
    {
      "id": "dotted"
    },
    {
      "id": "dashed"
    },
    {
      "id": "double"
    },
    {
      "id": "groove"
    },
    {
      "id": "ridge"
    },
    {
      "id": "inset"
    },
    {
      "id": "outset"
    }
  ];

  const decimalFormatTypes = (getEnvVar('ADM_DECIMAL_FORMAT_TYPES') || '').split(',') || [];

  const escapeHTMLAttributeValue = value => {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const dataFramePlugin = (editor, options) => {
    const getDfEmptyContent = () => {
      return `
      <div data-gjs-type="data-frame-component" data-class="data-frame" style="background-color: #EEE; color: #999; text-align: center;">
        ${editorStaticContent?.dataframe_empty_content || `DATA FRAME`}
      </div>
      `;
    };

    editor.BlockManager.add('data-frame-block', {
      label: editorStaticContent?.dataframe_empty_content || 'DATA FRAME',
      category: 'Advanced',
      attributes: { class: 'fa fa-table' },
      content: getDfEmptyContent()
    });

    editor.DomComponents.addType('data-frame-component', {
      isComponent: el => {
        if (el.dataset?.class === 'data-frame') {
          const result = { type: 'data-frame-component', item: {} };
          let extractedFields = {
            dataFrameName: '',
            dataFields: [],
            dataFieldKeys: []
          };
          let content = el.innerHTML;
          //Reverse Parsing dataFrame
          let matchedContent = content?.match(/{{!--(dataFrameField)(.*)--}}/g);
          if (matchedContent?.length) {
            let dataFrameFieldRegExp = new RegExp(
              '{{!--dataFrameField(.*)--}}'
            );
            let matchedDataFrameFields = dataFrameFieldRegExp.exec(
              matchedContent
            );
            if (matchedDataFrameFields?.length >= 2) {
              try {
                let parsedData = JSON.parse(matchedDataFrameFields[1]);
                extractedFields['dataFrameName'] = parsedData.name;
                let dataFieldList = parsedData.fields;
                extractedFields['productName'] = parsedData.productName;
                dataFieldList.forEach(dataField => {
                  extractedFields['dataFields'].push(dataField);
                  extractedFields['dataFieldKeys'].push(dataField.key);
                });
              } catch (error) {
                console.error('Error parsing dataFrame field JSON, removing corrupted element:', error);
                // Return false to remove the corrupted element
                return false;
              }
            }
          }

          let dataFields =
            (
              options.dataFields.filter(
                field => field.productName === extractedFields.productName
              )[0] ||
              options.dataFields.filter(field => (field.fieldFromSchema || field.schemaType === "EVENT"))[0] ||
              {}
            ).dataFrames?.filter(
              field => field.key === extractedFields.dataFrameName
            ) || {};
          if (Object.keys(dataFields).length > 0) {
            let dataField = dataFields[0];
            result.item = {
              dataFrameName: dataField.name,
              productName: extractedFields.productName,
              key: dataField.key,
              raw: [],
              customField: { type: 'Text', heading: '', value: '', iterate: 'None' }
            };
            dataField.subAttribute.forEach(field => {
              result.item.raw.push({
                heading: field.name,
                value: field.text,
                key: field.key,
                metaAttribute: field.metaAttribute,
                type: field.dataType
              });
            });
            result.item[dataField.name] = [...extractedFields.dataFields];
          }
          return result;
        }
      },
      model: {
        defaults: {
          testprop: 1,
          item: {},
          droppable: false,
          tableStyles: {
            textAlign: 'center',
            cellPadding: 8,
            borderStyle: 'solid',
            borderColor: '#ece9e9',
            borderWidthTop: 1,
            borderWidthBottom: 1,
            borderWidthLeft: 1,
            borderWidthRight: 1
          },
          tableHeaderStyles: {
            headerFontColor: '#000',
            headerFontSize: 14,
            headerBorderStyle: 'solid',
            headerBorderColor: '#ece9e9',
            headerBorderWidthTop: 1,
            headerBorderWidthBottom: 1,
            headerBorderWidthLeft: 1,
            headerBorderWidthRight: 1
          },
          tableValueStyles: {
            valueFontColor: '#000',
            valueFontSize: 12,
            valueBorderStyle: 'solid',
            valueBorderColor: '#ece9e9',
            valueBorderWidthTop: 1,
            valueBorderWidthBottom: 1,
            valueBorderWidthLeft: 1,
            valueBorderWidthRight: 1
          }
        },
        styles: ``,
        init() {
          this.listenTo(this, 'change:item', this.handlePropChange);
          this.listenTo(this, 'change:tableStyles', this.handlePropChange);
          this.listenTo(this, 'change:tableHeaderStyles', this.handlePropChange);
          this.listenTo(this, 'change:tableValueStyles', this.handlePropChange);
        },
        removed() {},
        handlePropChange() {
          updateCanvas(this.get('item'), true);
        }
      },
      view: {
        init() {},
        onRender() {
          let updatedItems = this.model.get('item');

          if (
            Object.keys(updatedItems).length > 0 &&
            updatedItems.dataFrameName
          ) {
            let tableStyles = this.model.get('tableStyles');
            let tableHeaderStyles = this.model.get('tableHeaderStyles');
            let tableValueStyles = this.model.get('tableValueStyles');
            let dfStyles = this.model.getStyle();
            let tableStyleMap = {
              '--df-table-text-align': 'textAlign',
              '--df-table-padding': 'cellPadding',
              '--df-table-border-color': 'borderColor',
              '--df-table-border-style': 'borderStyle',
              '--df-table-border-width-top': 'borderWidthTop',
              '--df-table-border-width-bottom': 'borderWidthBottom',
              '--df-table-border-width-left': 'borderWidthLeft',
              '--df-table-border-width-right': 'borderWidthRight'
            };
            let tableHeaderStylesMap = {
              '--df-table-header-font-color': 'headerFontColor',
              '--df-table-header-font-size': 'headerFontSize',
              '--df-table-header-border-color': 'headerBorderColor',
              '--df-table-header-border-style': 'headerBorderStyle',
              '--df-table-header-border-width-top': 'headerBorderWidthTop',
              '--df-table-header-border-width-bottom': 'headerBorderWidthBottom',
              '--df-table-header-border-width-left': 'headerBorderWidthLeft',
              '--df-table-header-border-width-right': 'headerBorderWidthRight'
            };
            let tableValueStylesMap = {
              '--df-table-value-font-color': 'valueFontColor',
              '--df-table-value-font-size': 'valueFontSize',
              '--df-table-value-border-color': 'valueBorderColor',
              '--df-table-value-border-style': 'valueBorderStyle',
              '--df-table-value-border-width-top': 'valueBorderWidthTop',
              '--df-table-value-border-width-bottom': 'valueBorderWidthBottom',
              '--df-table-value-border-width-left': 'valueBorderWidthLeft',
              '--df-table-value-border-width-right': 'valueBorderWidthRight'
            };

            Object.keys(tableStyleMap).forEach(styleProp => {
              let styleId = tableStyleMap[styleProp];
              if (dfStyles.hasOwnProperty(styleProp) && tableStyles.hasOwnProperty(styleId)) {
                let value = dfStyles[styleProp] || tableStyles[styleId];
                tableStyles[styleId] = value;
              }
            });
            Object.keys(tableHeaderStylesMap).forEach(styleProp => {
              let styleId = tableHeaderStylesMap[styleProp];
              if (dfStyles.hasOwnProperty(styleProp) && tableHeaderStyles.hasOwnProperty(styleId)) {
                let value = dfStyles[styleProp] || tableHeaderStyles[styleId];
                tableHeaderStyles[styleId] = value;
              }
            });
            Object.keys(tableValueStylesMap).forEach(styleProp => {
              let styleId = tableValueStylesMap[styleProp];
              if (dfStyles.hasOwnProperty(styleProp) && tableValueStyles.hasOwnProperty(styleId)) {
                let value = dfStyles[styleProp] || tableValueStyles[styleId];
                tableValueStyles[styleId] = value;
              }
            });

            this.model.set('tableStyles', tableStyles);
            this.model.set('tableHeaderStyles', tableHeaderStyles);
            this.model.set('tableValueStyles', tableValueStyles);

            updateCanvas(updatedItems, false, this.model);
          }
        }
      }
    });

    editor.StyleManager.addType('data-frame-property', {
      // Create UI
      create({ props, change }) {
        const el = document.createElement('div');
        el.innerHTML = `
        <div class="menu-item gjs-data-frame-wrapper" style="">
          <select class="data-frame-name" name="dataFrameName">
            <option value="" selected disabled hidden>${editorStaticContent?.dataframe_select_dataframe_label || `Select Data frame name`}</option>
            ${
              props.frames?.map(product => product.dataFrameNames || {}).concat().flat().filter(f => Object.keys(f).length > 0 && !f.hasNestedDataFrame && !f.metaAttribute).length !== 0
                ? props.frames.map(frame => {
                  if(frame?.dataFrameNames?.filter(f => Object.keys(f).length > 0 && !f.hasNestedDataFrame && !f.metaAttribute).length > 0) {
                    return `<optgroup label="${frame.productName}">
                      ${frame?.dataFrameNames?.filter(f => Object.keys(f).length > 0 && !f.hasNestedDataFrame && !f.metaAttribute).sort((a, b) => a?.label.localeCompare(b?.label)).map(f => {
                        return `<option value="${f.label}" data-productname="${frame.productName}">${f.label}</option>`;
                      })}
                      </optgroup>`;
                  }
                  })
                : `<option>${editorStaticContent?.dataframe_no_options || `No options`}</option>`
            }
          </select>
          <div class="gjs-data-frame-rows"></div>
        <div>
        `;
        const selectEl = el.querySelector('.data-frame-name');
        selectEl.addEventListener('change', event => change({ event })); // `change` will trigger the emit
        return el;
      },

      // Propagate UI changes up to the targets
      emit({ props, updateStyle }, { event, partial }) {
        const el = event.target;
        const { value } = el;
        let productName = event.target.selectedOptions[0]?.dataset?.productname;
        let dataField = (props.dataFields.filter(
          data => data.productName === productName
        ) || [])[0]?.dataFrames?.filter(d => {
          return d.name === value;
        })[0];
        let keyName = dataField.name;
        let getSelected = editor.getSelected();
        let updatedItems = {};
        if (getSelected?.get('type') === 'data-frame-component') {
          let selectedComponentItem = getSelected.get('item');
          if (
            selectedComponentItem?.dataFrameName &&
            selectedComponentItem.hasOwnProperty(value)
          ) {
            updatedItems = selectedComponentItem;
          }
        }
        if (Object.keys(updatedItems).length === 0) {
          updatedItems = {
            dataFrameName: keyName,
            key: dataField.key,
            productName: productName
          };
          updatedItems[keyName] = [];
          updatedItems['raw'] = dataField.subAttribute.map(attrib => {
            return {
              heading: attrib.name,
              value: attrib.text,
              key: attrib.key,
              metaAttribute: attrib.metaAttribute,
              type: attrib.dataType
            };
          });
          updatedItems['customField'] = {
            type: 'Text',
            heading: '',
            value: '',
            iterate: 'None'
          }
        }

        // updateStyle(`${value}px`, { partial });
        let rowsElement = el.parentElement.querySelector(
          '.gjs-data-frame-rows'
        );
        buildDfMenuRows(updatedItems, rowsElement);
        updateDfItem(updatedItems);
      },
      // Update UI (eg. when the target is changed)
      update({ value, el }) {
        let getSelected = editor.getSelected();
        if (getSelected?.get('type') === 'data-frame-component') {
          let updatedItems = getSelected.get('item');
          let rowsElement = el.querySelector('.gjs-data-frame-rows');
          if (updatedItems?.dataFrameName) {
            el.querySelector('.data-frame-name').value =
              updatedItems?.dataFrameName;
            buildDfMenuRows(updatedItems, rowsElement);
          } else {
            el.querySelector('.data-frame-name').value = '';
            buildDfMenuRows({}, rowsElement);
          }
        }
      },
      // Clean the memory from side effects if necessary (eg. global event listeners, etc.)
      destroy() {}
    });

    editor.on('component:selected', selComponent => {
      const styleManager = editor.StyleManager;

      let type = selComponent.get('type');
      if (type === 'data-frame-component') {
        // Bug 657594: Need to remove unused typography component in Dataframe and barcode classes
        selComponent.set({
          unstylable: ['font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align', 'text-shadow']
        });
        styleManager.addSector(
          'dfSector',
          {
            name: editorStaticContent?.dataframe_sector_name || 'Data Frame',
            properties: [
              {
                type: 'data-frame-property',
                property: 'data-frame-name',
                label: editorStaticContent?.dataframe_sector_name || 'Data Frame',
                frames: dataFrameKeys.namings || [],
                dataFields: dataFrameKeys.detailedList || [],
                dateFormatMenuOptions: dateFormatMenuOptions || []
              }
            ]
          },
          { at: 0 }
        );
        styleManager.addSector(
          'dfStyleSector',
          {
            name: 'Table Styles',
            open: false,
            properties: [
              {
                type: 'select',
                label: 'Text Alignment',
                property: '--df-table-text-align',
                isDataFrameProp: true,
                styleId: 'textAlign',
                default: 'center',
                options: [
                  { id: 'left', label: 'Left' },
                  { id: 'right', label: 'Right' },
                  { id: 'center', label: 'Center' },
                  { id: 'none', label: 'None' }
                ],
              },
              {
                type: 'number',
                label: 'Cell Padding',
                property: '--df-table-padding',
                isDataFrameProp: true,
                styleId: 'cellPadding',
                default: '8',
                units: ['px']
              },
              { 
                type: 'select',
                label: 'Border Style',
                property: '--df-table-border-style',
                isDataFrameProp: true ,
                styleId: 'borderStyle',
                options: borderStyleOptions || [],
                default: 'solid',
              },
              {
                type: 'color',
                label: 'Border Color',
                property: '--df-table-border-color',
                isDataFrameProp: true,
                styleId: 'borderColor',
                default: '#ece9e9'
              },
              { 
                type: 'number',
                label: 'Border Top Width',
                property: '--df-table-border-width-top',
                isDataFrameProp: true,
                styleId: 'borderWidthTop',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Bottom Width',
                property: '--df-table-border-width-bottom',
                isDataFrameProp: true,
                styleId: 'borderWidthBottom',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Left Width',
                property: '--df-table-border-width-left',
                isDataFrameProp: true,
                styleId: 'borderWidthLeft',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Right Width',
                property: '--df-table-border-width-right',
                isDataFrameProp: true,
                styleId: 'borderWidthRight',
                default: '0',
                units: ['px'],
              }
            ]
          },
          { at: 2 }
        );
        styleManager.addSector(
          'dfHeaderStyleSector',
          {
            name: 'Table Header Styles',
            open: false,
            properties: [
              {
                type: 'color',
                label: 'Header Font Color',
                property: '--df-table-header-font-color',
                isDataFrameHeaderProp: true,
                styleId: 'headerFontColor',
                default: '#000'
              },
              {
                type: 'number',
                label: 'Header Font Size',
                property: '--df-table-header-font-size',
                isDataFrameHeaderProp: true,
                styleId: 'headerFontSize',
                default: '14',
                units: ['px']
              },
              { 
                type: 'select',
                label: 'Border Style',
                property: '--df-table-header-border-style',
                isDataFrameHeaderProp: true ,
                styleId: 'headerBorderStyle',
                options: borderStyleOptions || [],
                default: 'solid',
              },
              {
                type: 'color',
                label: 'Border Color',
                property: '--df-table-header-border-color',
                isDataFrameHeaderProp: true,
                styleId: 'headerBorderColor',
                default: '#ece9e9'
              },
              { 
                type: 'number',
                label: 'Border Top Width',
                property: '--df-table-header-border-width-top',
                isDataFrameHeaderProp: true,
                styleId: 'headerBorderWidthTop',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Bottom Width',
                property: '--df-table-header-border-width-bottom',
                isDataFrameHeaderProp: true,
                styleId: 'headerBorderWidthBottom',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Left Width',
                property: '--df-table-header-border-width-left',
                isDataFrameHeaderProp: true,
                styleId: 'headerBorderWidthLeft',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Right Width',
                property: '--df-table-header-border-width-right',
                isDataFrameHeaderProp: true,
                styleId: 'headerBorderWidthRight',
                default: '0',
                units: ['px'],
              }
            ]
          },
          { at: 3 }
        );
        styleManager.addSector(
          'dfValueStyleSector',
          {
            name: 'Table Value Styles',
            open: false,
            properties: [
              {
                type: 'color',
                label: 'Value Font Color',
                property: '--df-table-value-font-color',
                isDataFrameValueProp: true,
                styleId: 'valueFontColor',
                default: '#000'
              },
              {
                type: 'number',
                label: 'Value Font Size',
                property: '--df-table-value-font-size',
                isDataFrameValueProp: true,
                styleId: 'valueFontSize',
                default: '12',
                units: ['px']
              },
              { 
                type: 'select',
                label: 'Border Style',
                property: '--df-table-value-border-style',
                isDataFrameValueProp: true ,
                styleId: 'valueBorderStyle',
                options: borderStyleOptions || [],
                default: 'solid',
              },
              {
                type: 'color',
                label: 'Border Color',
                property: '--df-table-value-border-color',
                isDataFrameValueProp: true,
                styleId: 'valueBorderColor',
                default: '#ece9e9'
              },
              { 
                type: 'number',
                label: 'Border Top Width',
                property: '--df-table-value-border-width-top',
                isDataFrameValueProp: true,
                styleId: 'valueBorderWidthTop',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Bottom Width',
                property: '--df-table-value-border-width-bottom',
                isDataFrameValueProp: true,
                styleId: 'valueBorderWidthBottom',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Left Width',
                property: '--df-table-value-border-width-left',
                isDataFrameValueProp: true,
                styleId: 'valueBorderWidthLeft',
                default: '0',
                units: ['px'],
              },
              {
                type: 'number',
                label: 'Border Right Width',
                property: '--df-table-value-border-width-right',
                isDataFrameValueProp: true,
                styleId: 'valueBorderWidthRight',
                default: '0',
                units: ['px'],
              }
            ]
          },
          { at: 4 }
        );
        let dfSector = styleManager.getSector('dfSector');
        dfSector.set('open', true);

        setTimeout(() => {
          let tableStyles = selComponent.get('tableStyles');
          let tableHeaderStyles = selComponent.get('tableHeaderStyles');
          let tableValueStyles = selComponent.get('tableValueStyles');
          let dfSectorProps = styleManager.getProperties('dfSector');
          let dfStyleSectorProps = styleManager.getProperties('dfStyleSector');
          let dfHeaderStyleSectorProps = styleManager.getProperties('dfHeaderStyleSector');
          let dfValueStyleSectorProps = styleManager.getProperties('dfValueStyleSector');
          let dfProperty = dfSectorProps.models.filter(
            prop => prop.get('type') === 'data-frame-property'
          )[0];

          dfStyleSectorProps?.models.forEach(property => {
            let value =
              property.attributes.type === 'number'
                ? parseInt(property.attributes.value || property.attributes.default)
                : (property.attributes.value || property.attributes.default);
            tableStyles[property.attributes.styleId] = value;
            property.upValue(value);
          });

          dfHeaderStyleSectorProps?.models.forEach(property => {
            let value =
              property.attributes.type === 'number'
                ? parseInt(property.attributes.value || property.attributes.default)
                : (property.attributes.value || property.attributes.default);
            tableHeaderStyles[property.attributes.styleId] = value;
            property.upValue(value);
          });

          dfValueStyleSectorProps?.models.forEach(property => {
            let value =
              property.attributes.type === 'number'
                ? parseInt(property.attributes.value || property.attributes.default)
                : (property.attributes.value || property.attributes.default);
            tableValueStyles[property.attributes.styleId] = value;
            property.upValue(value);
          });

          dfProperty.trigger('change:value');
          editor.on('style:property:update', handleTableStyleUpdate);
          editor.on('style:property:update', handleTableHeaderStyleUpdate);
          editor.on('style:property:update', handleTableValueStyleUpdate);
        }, 250);
      } else {
        styleManager.removeSector('dfSector');
        styleManager.removeSector('dfStyleSector');
        styleManager.removeSector('dfHeaderStyleSector');
        styleManager.removeSector('dfValueStyleSector');
      }
    });

    editor.on('component:deselected', selComponent => {
      let type = selComponent.get('type');
      if (type === 'data-frame-component') {
        editor.off('style:property:update', handleTableStyleUpdate);
        editor.off('style:property:update', handleTableHeaderStyleUpdate);
        editor.off('style:property:update', handleTableValueStyleUpdate);
      }
    });

    editor.on('load', () => {
      editor.on('component:add', component => {
        if (component.get('type') === 'data-frame-component') {
          let dataFrameName = component.attributes.item.dataFrameName;
          let updatedValidation = {};
          updatedValidation[component.cid] = {isDisabled: true};
          if (dataFrameName && (component.attributes.item[dataFrameName]).length > 0) {
            updatedValidation[component.cid] = {isDisabled: false};
          }
          setIsValidationError(prevState => ({
            ...prevState,
            ...updatedValidation
          }));
        }
      });
      editor.on('component:remove', component => {
        if (component.get('type') === 'data-frame-component') {
          setIsValidationError(prevState => {
            delete prevState[component.cid];
            return { ...prevState };
          });
        }
      });
      editor.on('component:update', component => {
        if (component.get('type') === 'data-frame-component') {
          let isDirty = true;
          let dataFrameName = component.attributes.item.dataFrameName;
          if (dataFrameName && (component.attributes.item[dataFrameName]).length > 0) {
            isDirty = false;
          }
          if (isDirty?.length > 0 || isDirty === true) {
            let updatedValidation = {};
            updatedValidation[component.cid] = {isDisabled: true};
            setIsValidationError(prevState => ({
              ...prevState,
              ...updatedValidation
            }));
          } else {
            let updatedValidation = {};
            updatedValidation[component.cid] = {isDisabled: false};
            setIsValidationError(prevState => ({
              ...prevState,
              ...updatedValidation
            }));
          }
        }
      });
    });

    const handleTableStyleUpdate = model => {
      let selComponent = editor.getSelected();
      let { property } = model;

      if (
        selComponent.get('type') === 'data-frame-component' &&
        property.attributes?.isDataFrameProp
      ) {
        let value = property.getValue();
        let tableStyles = selComponent.get('tableStyles');

        tableStyles[property.attributes.styleId] =
          property.attributes.type === 'number' ? parseInt(value) : value;
        selComponent.set('tableStyles', tableStyles);
        selComponent.trigger('change:tableStyles');
        
        let item = selComponent.get('item');
        let updatedItem = { ...item, tableStyles };
        selComponent.set('item', updatedItem);

      }
    };
    
    const handleTableHeaderStyleUpdate = model => {
      let selComponent = editor.getSelected();
      let { property } = model;
      if (
        selComponent.get('type') === 'data-frame-component' &&
        property.attributes?.isDataFrameHeaderProp
      ) {
        let value = property.getValue();
        let tableHeaderStyles = selComponent.get('tableHeaderStyles');
        tableHeaderStyles[property.attributes.styleId] =
          property.attributes.type === 'number' ? parseInt(value) : value;
        selComponent.set('tableHeaderStyles', tableHeaderStyles);
        selComponent.trigger('change:tableHeaderStyles');
        let item = selComponent.get('item');
        let updatedItem = { ...item, tableHeaderStyles };
        selComponent.set('item', updatedItem);
      }
    };

    const handleTableValueStyleUpdate = model => {
      let selComponent = editor.getSelected();
      let { property } = model;
      if (
        selComponent.get('type') === 'data-frame-component' &&
        property.attributes?.isDataFrameValueProp
      ) {
        let value = property.getValue();
        let tableValueStyles = selComponent.get('tableValueStyles');
        tableValueStyles[property.attributes.styleId] =
          property.attributes.type === 'number' ? parseInt(value) : value;
        selComponent.set('tableValueStyles', tableValueStyles);
        selComponent.trigger('change:tableValueStyles');
        let item = selComponent.get('item');
        let updatedItem = { ...item, tableValueStyles };
        selComponent.set('item', updatedItem);
      }
    };

    const updateCanvas = (item, updateApi, component) => {
      let selectedComponent = component ? component : editor.getSelected();
      let tableStyles = { ...selectedComponent.get('tableStyles'), ...(item.tableStyles || {}) };
      let tableHeaderStyles = { ...selectedComponent.get('tableHeaderStyles'), ...(item.tableHeaderStyles || {}) };
      let tableValueStyles = { ...selectedComponent.get('tableValueStyles'), ...(item.tableValueStyles || {}) };
      let keyName = item.dataFrameName;

      if (!item[keyName] || item[keyName]?.length === 0) {
        selectedComponent.components('DATA FRAME');
        selectedComponent.view.el.innerHTML = editorStaticContent?.dataframe_empty_content || 'DATA FRAME';
      } else if (options.isLoading) {
        selectedComponent.components('DATA FRAME');
        selectedComponent.view.el.innerHTML = editorStaticContent?.dataframe_loading || 'Loading...';
      } else {
        // Sent to api
        if ( updateApi) {
          selectedComponent.components(`
            <div>
              {{!--dataFrameField${JSON.stringify({
                name: item.key,
                fields: item[keyName],
                productName: item.productName
              })}--}}
              <div style="display:none">{{#if ${item.key}}}</div>
              <table 
                style="border-collapse: collapse;
                border-top: ${parseInt(tableStyles.borderWidthTop)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
                border-bottom: ${parseInt(tableStyles.borderWidthBottom)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
                border-left: ${parseInt(tableStyles.borderWidthLeft)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
                border-right: ${parseInt(tableStyles.borderWidthRight)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
                table-layout: fixed;
                width: 100%;">
                <thead>
                  <tr>
                    ${item[keyName]
                      .map(i => {
                        return `
                        <th 
                          style="border-top: ${parseInt(tableHeaderStyles.headerBorderWidthTop)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                          border-bottom: ${parseInt(tableHeaderStyles.headerBorderWidthBottom)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                          border-left: ${parseInt(tableHeaderStyles.headerBorderWidthLeft)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                          border-right: ${parseInt(tableHeaderStyles.headerBorderWidthRight)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                          color: ${tableHeaderStyles.headerFontColor};
                          font-size: ${parseInt(tableHeaderStyles.headerFontSize)}px;
                          text-align:${i.alignment || tableStyles.textAlign};
                          padding: ${parseInt(tableStyles.cellPadding)}px;
                          overflow-wrap: break-word;
                          ${i['width'] ? `width: ${i['width']}%;` : ``}">
                            ${i['heading'].toUpperCase()}
                        </th>`;
                      })
                      .join('')}
                  </tr>
                </thead>
                <tbody>
                  <tr style="display:none"><td>{{#each ${item.key}}}</td></tr>
                  <tr style="border-top: ${parseInt(tableValueStyles.valueBorderWidthTop)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                      border-bottom: none;
                      border-left: ${parseInt(tableValueStyles.valueBorderWidthLeft)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                      border-right: ${parseInt(tableValueStyles.valueBorderWidthRight)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};">
                    ${item[keyName]
                      .map(i => {
                        if (i.type === 'Date') {
                          i.prefix = new RegExp(`.*(?={{${i.key}}})`).exec(
                            i.value
                          )?.[0];
                          i.suffix = new RegExp(`(?<={{${i.key}}}).*`).exec(
                            i.value
                          )?.[0];
                        }
                        return `
                        <td class="highlight"
                          style="border-top: ${parseInt(tableValueStyles.valueBorderWidthTop)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                          border-bottom: none;
                          border-left: ${parseInt(tableValueStyles.valueBorderWidthLeft)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                          border-right: ${parseInt(tableValueStyles.valueBorderWidthRight)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                          color: ${tableValueStyles.valueFontColor};
                          font-size: ${tableValueStyles.valueFontSize}px;
                          text-align:${i.alignment || tableStyles.textAlign};
                          padding: ${parseInt(tableStyles.cellPadding)}px;
                          overflow-wrap: break-word;
                          ${i['width'] ? `width: ${i['width']}%;` : ``}">
                            ${i.iterate && i.iterate !== 'None' ? `<span class="jsx-syntax">{{#each ${i.iterate}}}</span>` : ``}
                              <div>
                                ${i.prefix ? i.prefix : ``}
                                ${i.type === 'Date' 
                                  ? `{{dateFormat ${i.key}${i.dateFormat ?` format="${i.dateFormat}"` : ``}${i.timeZone ? ` tz="${i.timeZone}"` : ``}}}` 
                                  : (i.type === 'Image' 
                                      ? `<img src="${i.value}" style="width: 100%;"/>` 
                                      : (decimalFormatTypes?.includes(i.type) && i.decimalPoint
                                          ? `${i.value}`.replace('{{', '{{decimalFormat ')
                                          : `${i.value}`))
                                }
                                ${i.suffix ? i.suffix : ``}
                              </div>
                            ${i.iterate && i.iterate !== 'None' ? `<span class="jsx-syntax">{{/each}}</span>` : ``}
                        </td>`;
                      }).join('')}
                  </tr>
                  <tr style="display:none"><td>{{#if subscript}}</td></tr>
                    <tr style="display:none"><td>{{#each subscript}}</td></tr>
                    <tr style="border-top: none;
                        border-bottom: none;
                        border-left: ${parseInt(tableValueStyles.valueBorderWidthLeft)}px ${tableValueStyles.valueBorderStyle}  ${tableValueStyles.valueBorderColor};
                        border-right: ${parseInt(tableValueStyles.valueBorderWidthRight)}px ${tableValueStyles.valueBorderStyle}  ${tableValueStyles.valueBorderColor};">
                    ${item[keyName].map(i => {
                      return `
                        <td class="subscript"
                          style="border-top: none;
                          border-bottom: none;
                          border-left: ${parseInt(tableValueStyles.valueBorderWidthLeft)}px ${tableValueStyles.valueBorderStyle}  ${tableValueStyles.valueBorderColor};
                          border-right: ${parseInt(tableValueStyles.valueBorderWidthRight)}px ${tableValueStyles.valueBorderStyle}  ${tableValueStyles.valueBorderColor};
                          color: ${tableValueStyles.valueFontColor};
                          font-size: ${tableValueStyles.valueFontSize}px;
                          text-align:${i.alignment || tableStyles.textAlign};
                          padding: ${parseInt(tableStyles.cellPadding)}px;
                          overflow-wrap: break-word;
                          ${i['width'] ? `width: ${i['width']}%;` : ``}">
                          ${i.iterate ? `` : `<div>{{this.${i['key']}}}</div>`}
                        </td>`;
                      }).join('')}
                      </tr>
                    <tr style="display:none"><td>{{/each}}</td></tr>
                  <tr style="display:none"><td>{{/if}}</td></tr>
                  <tr style="display:none"><td>{{/each}}</td></tr>
                </tbody>
              </table>
              <div style="display:none">{{/if}}</div>
            </div>`);
          }

        //To display in context
        selectedComponent.view.el.innerHTML = `
          <div>
            <table 
              style="border-collapse: collapse;
              border-top: ${parseInt(tableStyles.borderWidthTop)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
              border-bottom: ${parseInt(tableStyles.borderWidthBottom)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
              border-left: ${parseInt(tableStyles.borderWidthLeft)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
              border-right: ${parseInt(tableStyles.borderWidthRight)}px ${tableStyles.borderStyle} ${tableStyles.borderColor};
              table-layout: fixed;
              width: 100%;">
              <thead>
                <tr>
                  ${item[keyName]
                    .map(i => {
                      return `
                      <th 
                        style="border-top: ${parseInt(tableHeaderStyles.headerBorderWidthTop)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                        border-bottom: ${parseInt(tableHeaderStyles.headerBorderWidthBottom)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                        border-left: ${parseInt(tableHeaderStyles.headerBorderWidthLeft)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                        border-right: ${parseInt(tableHeaderStyles.headerBorderWidthRight)}px ${tableHeaderStyles.headerBorderStyle} ${tableHeaderStyles.headerBorderColor};
                        color: ${tableHeaderStyles.headerFontColor};
                        font-size: ${tableHeaderStyles.headerFontSize}px;
                        text-align:${i.alignment || tableStyles.textAlign};
                        padding: ${parseInt(tableStyles.cellPadding)}px;
                        overflow-wrap: break-word;
                        ${i['width'] ? `width: ${i['width']}%;` : ``}">
                          ${i['heading'].toUpperCase()}
                      </th>`;
                    })
                    .join('')}
                </tr>
              </thead>
              <tbody>
                <tr>
                  ${item[keyName]
                    .map(i => {
                      return `
                      <td 
                        style="border-top: ${parseInt(tableValueStyles.valueBorderWidthTop)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                        border-bottom: ${parseInt(tableValueStyles.valueBorderWidthBottom)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                        border-left: ${parseInt(tableValueStyles.valueBorderWidthLeft)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                        border-right: ${parseInt(tableValueStyles.valueBorderWidthRight)}px ${tableValueStyles.valueBorderStyle} ${tableValueStyles.valueBorderColor};
                        color: ${tableValueStyles.valueFontColor};
                        font-size: ${tableValueStyles.valueFontSize}px;
                        text-align:${i.alignment || tableStyles.textAlign};
                        padding: ${parseInt(tableStyles.cellPadding)}px;
                        overflow-wrap: break-word;
                        ${i['width'] ? `width: ${i['width']}%;` : ``}">
                          ${i.type === 'Image' ? `<img src="${i.value}" style="width: 100%;"/>` : `${i.value}`}
                          ${
                            i.type === 'Date'
                              ? `<div>${
                                  i.dateFormat
                                    ? ` (format="${i.dateFormat}"`
                                    : ``
                                }${
                                  i.timeZone ? `, tz="${i.timeZone})"` : `)`
                                }</div>`
                              : ``
                          }
                      </td>`;
                    })
                    .join('')}
                </tr>
              </tbody>
            </table>
          </div>`;
      }
    };

    const handleAddBtn = (items, el, event) => {
      let prev = Object.assign({}, items);
      let itemIndex = event.target.dataset.index;
      let keyName = items.dataFrameName;

      let newItem = [];
      prev['raw'] = items['raw']?.filter(f => !f.metaAttribute).map(function(data, i) {
        if (i == itemIndex) {
          newItem.push({ ...data });
          return {
            heading: data.heading,
            value: data.value,
            key: data.key,
            metaAttribute: data.metaAttribute,
            type: data.type
          };
        }
        return { ...data };
      });
      prev[keyName] = [...prev[keyName], ...newItem];
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleDeleteBtn = (items, el, event) => {
      let prev = Object.assign({}, items);
      let itemIndex = event.target.dataset.index;
      let keyName = items.dataFrameName;

      let updatedItems = items[keyName]
        .map(function(data, i) {
          if (i != itemIndex) {
            return data;
          }
        })
        .filter(function(d) {
          return d;
        });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleMoveUpBtn = (items, el, event) => {
      let prev = Object.assign({}, items);
      let itemIndex = Number(event.target.dataset.index);
      let keyName = items.dataFrameName;

      let updatedItems = items[keyName]
        .map(function(data, i) {
          if (i == itemIndex - 1) {
            return items[keyName][itemIndex];
          }
          if (i == itemIndex) {
            return items[keyName][itemIndex - 1];
          }
          return data;
        })
        .filter(function(d) {
          return d;
        });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleMoveDownBtn = (items, el, event) => {
      let prev = Object.assign({}, items);
      let itemIndex = Number(event.target.dataset.index);
      let keyName = items.dataFrameName;

      let updatedItems = items[keyName]
        .map(function(data, i) {
          if (i == itemIndex) {
            return items[keyName][itemIndex + 1];
          }
          if (i == itemIndex + 1) {
            return items[keyName][itemIndex];
          }
          return data;
        })
        .filter(function(d) {
          return d;
        });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleDateFormatChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let itemIndex = event.target.dataset.index;
      let fieldName = event.target.name;
      let keyName = item.dataFrameName;
      let updatedItems = [];
      if (fieldName === 'addedFields') {
        updatedItems = prev[keyName].map(function(data, i) {
          if (i == itemIndex) {
            if (event.target.selectedOptions[0].dataset.timezone) {
              data.timeZone = event.target.selectedOptions[0].dataset.timezone;
            } else {
              delete data.timeZone;
            }
            return {
              ...data,
              dateFormat: event.target.value
            };
          }
          return { ...data };
        });
        prev[keyName] = updatedItems;
      }
      if (fieldName === 'rawFields') {
        updatedItems = prev['raw'].map(function(data, i) {
          if (i == itemIndex) {
            if (event.target.selectedOptions[0].dataset.timezone) {
              data.timeZone = event.target.selectedOptions[0].dataset.timezone;
            } else {
              delete data.timeZone;
            }
            return {
              ...data,
              dateFormat: event.target.value
            };
          }
          return { ...data };
        });
        prev['raw'] = updatedItems;
      }
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleColumnHeadingChange = (el, event) => {
      let items = editor.getSelected().get('item');
      let prev = Object.assign({}, items);
      let itemIndex = event.target.dataset.index;
      let keyName = items.dataFrameName;
      let updatedItems = items[keyName].map(function(item, i) {
        if (i == itemIndex) {
          if (item.isDirty && Object.keys(item.isDirty).length === 0) {
            delete item.isDirty;
          }
          return {
            ...item,
            heading: escapeHTMLAttributeValue(event.target.value)
          };
        } else {
          return { ...item };
        }
      });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleColumnValueChange = (el, event) => {
      let items = editor.getSelected().get('item');
      let prev = Object.assign({}, items);
      let itemIndex = event.target.dataset.index;
      let keyName = items.dataFrameName;
      let updatedItems = items[keyName].map(function(item, i) {
        if (i == itemIndex) {
          if (item.isDirty && Object.keys(item.isDirty).length === 0) {
            delete item.isDirty;
          }
          return {
            ...item,
            value: escapeHTMLAttributeValue(event.target.value)
          };
        } else {
          return { ...item };
        }
      });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleDecimalValueChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let { checked } = event.target;
      let { key, index } = event.target.dataset;
      let keyName = item?.dataFrameName || '';

      let updatedItems = prev[keyName].map((attrib, i) => {
        if (attrib.key === key && i == index) {
          if (checked) {
            attrib.decimalPoint = 2;
          } else {
            delete attrib.decimalPoint;
          }
        }
        return attrib;
      });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleAlignmentChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let { value } = event.target;
      let itemIndex = event.target.dataset.index;
      let keyName = item?.dataFrameName;

      let updatedItems = prev[keyName].map((attrib, i) => {
        if (i == itemIndex) {
          if (value === 'None') {
            delete attrib.alignment;
          } else {
            attrib.alignment = value;
          }
        }
        return attrib;
      });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleColumnWidthChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let itemIndex = event.target.dataset.index;
      let keyName = item?.dataFrameName;
      let updatedItems = prev[keyName].map((attrib, i) => {
        if (i == itemIndex) {
          let numberRegex = new RegExp(`^[1-9][0-9]?$|^100$|$^`);
          let isWidthDirty = numberRegex.exec(event.target.value);
          if (isWidthDirty === null) {
            attrib = {
              ...attrib,
              isDirty: { ...attrib.isDirty, widthDirty: true }
            };
          } else if (attrib.isDirty?.widthDirty) {
            delete attrib.isDirty?.widthDirty;
          }
          if (attrib.isDirty && Object.keys(attrib.isDirty).length === 0) {
            delete attrib.isDirty;
          }
          return {
            ...attrib,
            width: escapeHTMLAttributeValue(event.target.value)
          };
        } else {
          return { ...attrib };
        }
      });
      prev[keyName] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleCustomFieldTypeChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let updatedType = event.target.value;
      let updatedItems = { type: updatedType, heading: '', value: '', iterate: item.customField.iterate };
      prev['customField'] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleCustomIterateChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let updatedIterate = event.target.value;
      let productName = event.target.selectedOptions[0]?.dataset?.productname;
      const selectedDataFrame = (dataFrameKeys.detailedList.find(
        data => data.productName === productName
      )?.dataFrames || []).find(d => d.key === updatedIterate);
      let updatedItems = { 
        type: item.customField.type, 
        heading: item.customField.heading, 
        value: item.customField.value, 
        iterate: updatedIterate,
        raw: (selectedDataFrame?.subAttribute || []).map(attrib => {
          return {
            heading: attrib.name,
            value: attrib.text,
            key: attrib.key,
            metaAttribute: attrib.metaAttribute,
            type: attrib.dataType
          };
        }),
        iterateName: selectedDataFrame?.name
      };
      prev['customField'] = updatedItems;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleCustomHeadingChange = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let fieldValue = {
        ...item.customField, 
        heading: escapeHTMLAttributeValue(event.target.value)
      };
      if (fieldValue.isDirty && Object.keys(fieldValue.isDirty).length === 0) {
        delete fieldValue.isDirty;
      }
      prev['customField'] = fieldValue;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleCustomValueChange = async (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let fieldValue = {
        ...item.customField, 
        value: event.target.value
      };
      if(item?.customField?.type === "Image") {
        if(acceptTypes.indexOf(event.target?.files[0]?.type) >= 0) {
           const file = event.target?.files[0];
           if(file.size > acceptLimit) {
             fieldValue = {...item.customField, value: ''};
             openSnackBarError(
               'error',
               `${editorStaticContent?.dataframe_error_image_format[0] || `Size of`} ${file?.name} ${editorStaticContent?.dataframe_error_image_format[1] || `is`} ${returnFileSize(file?.size)}. ${editorStaticContent?.dataframe_error_image_format[2] || `Maximum allowed size is`} ${acceptLimitLabel} MB`
             );
             document.getElementById('customFile').value = null;
           } else {
             try {
               const endpoint = getContentServiceUploadApiRoot(currentTenantId, currentBusinessContextId);
               const customTag = getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm';
               const formData = new FormData();
               formData.append('file', file);
                const response = await axios.post(endpoint, formData, {
                params: {
                  customTag: customTag
                }
              });
               const cdnUrl = response?.data?.cdnPublicUrl || response?.data?.publicUrl || response?.data?.filelink || response?.data?.url;
               if (cdnUrl) {
                 dispatch({ type: uploadActions.GET_UPLOADS });
                 document.getElementById('customImage').setAttribute('src', cdnUrl);
                 fieldValue = {...item.customField, value: cdnUrl, imageName: file?.name};
               } else {
                 openSnackBarError('error', `${editorStaticContent?.cdn_url_not_found}` || 'Upload successful but CDN URL not found in response.');
                 fieldValue = {...item.customField, value: ''};
               }
             } catch (error) {
               const responseData = error?.response?.data;
               const isDuplicate = error?.response?.status === 409 || responseData?.status === 409 || responseData?.code === 4002;
               const message = isDuplicate
                 ? (editorStaticContent?.image_duplicate_error || 'Image already exists. Try with a different name.')
                 : (responseData?.message || editorStaticContent?.image_upload_error || 'Failed to upload image.');
               openSnackBarError('error', message);
               fieldValue = {...item.customField, value: ''};
             }
             document.getElementById('customFile').value = null;
           }
        } else {
          fieldValue = {...item.customField, value: ''};
          openSnackBarError(
            'error',
            `${event.target?.files[0]?.name} ${editorStaticContent?.dataframe_error_image_size[0] || `is not supported`}. ${acceptTypesLabel}`
          );
          document.getElementById('customFile').value = null;
        }
      } else {
        fieldValue = {...item.customField, value: escapeHTMLAttributeValue(event.target.value)};
      }
      if (fieldValue.isDirty && Object.keys(fieldValue.isDirty).length === 0) {
        delete fieldValue.isDirty;
      }
      prev['customField'] = fieldValue;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleRemoveImage = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let fieldValue = {
        ...item.customField,
        value: ''
      };
      prev['customField'] = fieldValue;
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleAddCustomField = (el, event) => {
      let item = editor.getSelected().get('item');
      let prev = Object.assign({}, item);
      let keyName = item.dataFrameName;
      let newItem = [{
        ...item.customField,
        key: item.customField.value,
        isCustom: true
      }];
      prev[keyName] = [ ...prev[keyName], ...newItem ];
      prev['customField'] = {
        type: 'Text',
        heading: '',
        value: '',
        iterate: 'None'
      };
      buildDfMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleCustomTagClick = (el, event) => {
      event.preventDefault();
      navigator.clipboard.writeText(event.target.value);
    };

    const updateDfItem = updatedItems => {
      let selectedComponent = editor.getSelected();
      selectedComponent.set('item', updatedItems);
      selectedComponent.trigger('change:item');
    };

    const buildDfMenuRows = (items, el) => {
      let keyName = Object.keys(items).length > 0 && items.dataFrameName;
      let rows = [];
      const fieldsRow = (attrib, index, isAdd) => {
        const dateRow = (attrib, index) => {
          let row = '';
          if (attrib.type === 'Date') {
            row = `
              <div class="gjs-sm-label" data-sm-label="">
                <span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_column_dateformat_label || `Date Format`}</span>
              </div>
              <select class="data-frame-date-format df-select" name="${
                isAdd ? 'rawFields' : 'addedFields'
              }" data-index="${index}" data-key="${attrib.key}">
                <option value="" selected disabled hidden>${editorStaticContent?.dataframe_select_dateformat_label || `Select Date Format`}</option>
                ${dateFormatMenuOptions.map(f => {
                  return `<option value="${f.value}" ${
                    f.timeZone ? `data-timezone="${f.timeZone}"` : ``
                  } ${attrib.dateFormat === f.value &&
                    attrib.timeZone === f.timeZone &&
                    'selected'}>Ex: ${f.displayExample} (${f.value})</option>`;
                })}
              </select>
            `;
          }
          return row;
        };

        const alignmentRow = (attrib, index) => {
          let row = `
            <div class="gjs-sm-label" data-sm-label="">
              <span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_column_alignment_label || `Column Alignment`}</span>
            </div>
            <select class="gjs-data-frame-column-alignment df-select" name="${
              isAdd ? 'rawFields' : 'addedFields'
            }" data-index="${index}" data-key="${attrib.key}">
              ${alignmentOptions.map(f => {
                return `<option value="${f.value}"
                  ${attrib.alignment === f.value && 'selected'}>${
                  f.value
                }</option>`;
              })}
            </select>
          `;
          return row;
        };

        const resizeRow = (attrib, index) => {
          let row = `
            <div>
              <div class="gjs-sm-label" data-sm-label="">
                <span class="gjs-sm-icon" title="">${
                  attrib.isDirty?.widthDirty
                    ? `${editorStaticContent?.dataframe_column_width_label || `Column width (in %)`}<span style="color: red; padding-left: 4px;">${editorStaticContent?.dataframe_error_column_width || `should only contain numbers from 1 to 100`}<span>`
                    : `${editorStaticContent?.dataframe_column_width_label || `Column width (in %)`}`
                }</span>
              </div>
              <input class="gjs-data-frame-column-width" data-index="${index}" type="text" value="${attrib.width ||
            ''}" style="width: 100%"/>
            </div>
          `;
          return row;
        };

        const roundOffOption = (attrib, index) => {
          let row = '';
          if (decimalFormatTypes.includes(attrib.type)) {
            row = `
              <div class="gjs-sm-label gjs-decimal-point">
                <input class="gjs-data-frame-column-decimal" type="checkbox" ${
                  attrib.decimalPoint ? 'checked ' : ''
                }name="${attrib.key}" data-index="${index}" data-key="${
              attrib.key
            }">
                <label>${editorStaticContent?.dataframe_column_decimal_label || `Round off to 2 decimal points`}</label>
              </div>
            `;
          }
          return row;
        };

        let addBtnStyles = {
          color: 'red',
          cursor: 'pointer',
          pointerEvents: 'all'
        };

        if (attrib.type === 'Date' && !attrib?.dateFormat) {
          addBtnStyles.color = '#aaa';
          addBtnStyles.cursor = 'default';
          addBtnStyles.pointerEvents = 'none';
        }

        return `
          ${(isAdd != true && attrib.iterate) ? `<div class="menu-item gjs-data-frame-wrapper" style="">
            <div class="gjs-sm-label" data-sm-label="">
              <span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_iterate_label || `Iterate`}</span>
            </div>
            <span class="gjs-data-frame-label" data-index="${index}">${attrib.iterateName || 'NONE'}</span>
          </div>` : ``}
          <div class="gjs-data-frame-block">
            <div class="gjs-sm-label" data-sm-label="">
              <span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_column_name_label || `Column Name`}</span>
            </div>
            ${
              isAdd === true
                ? `<span class="gjs-data-frame-label" data-index="${index}">${attrib.heading}</span>`
                : `<input class="gjs-data-frame-column-heading" data-index="${index}" value="${attrib.heading}" style="width: 100%"/>`
            }
            <div class="gjs-sm-label" data-sm-label="">
              <span class="gjs-sm-icon" title="">${
                isAdd !== true
                  ? `${attrib.type !== 'Image' 
                    ? `${editorStaticContent?.dataframe_column_value_label || `Value`}`
                    : `<div class="blockbuilder-widget-label pt-2">
                        <label>${editorStaticContent?.dataframe_column_image_label || `Image`}</label>
                      </div>`
                    }`
                  : `${editorStaticContent?.dataframe_column_value_label || `Value`}`
              }</span>
            </div>
            ${
              isAdd === true
                ? `<span class="gjs-data-frame-label" data-index="${index}">${attrib.value}</span>`
                : `${attrib.type !== 'Image' 
                  ? `<input class="gjs-data-frame-column-value" data-index="${index}" value="${attrib.value}" style="width: 100%"/>`
                  : `<img src="${attrib.value}" style="width: 150px;"/>`}`
            }
            ${dateRow(attrib, index)}
            ${!isAdd ? alignmentRow(attrib, index) : ''}
            ${!isAdd ? resizeRow(attrib, index) : ''}
            ${!isAdd ? roundOffOption(attrib, index) : ''}
            <div style="display: flex; justify-content: space-between;">
              <a class="${
                isAdd === true ? 'add-btn' : 'delete-btn'
              }" data-index="${index}" 
                style="display: inline-block; 
                ${
                  attrib.type === 'Date' && !attrib.dateFormat
                    ? 'cursor: default; pointer-events: none; color: #aaa;'
                    : 'cursor: pointer; color: red;'
                }
                margin-top: 10px; font-size: 12px;">
                ${isAdd === true ? `${editorStaticContent?.dataframe_column_add_item_label || `Add Item`}` : `${editorStaticContent?.dataframe_column_delete_item_label || `Delete Item`}`}
              </a>
              ${
                !isAdd
                  ? `<div style="display: flex; column-gap: 8px">
                <a class="move-up-btn" data-index="${index}" style="pointer-events: ${
                      index > 0 ? `all` : `none`
                    }; cursor: pointer; margin-top: 10px; font-size: 12px; color: ${
                      index > 0 ? `#43a047` : `rgb(143, 150, 153)`
                    };">
                  ${editorStaticContent?.dataframe_column_moveup_label || `Move up`}
                </a>
                <a class="move-down-btn" data-index="${index}" style="pointer-events: ${
                      index < items[keyName].length - 1 ? `all` : `none`
                    }; cursor: pointer; margin-top: 10px; font-size: 12px; color: ${
                      index < items[keyName].length - 1
                        ? `#43a047`
                        : `rgb(143, 150, 153)`
                    };">
                  ${editorStaticContent?.dataframe_column_movedown_label || `Move Down`}
                </a>
              </div>`
                  : ``
              }
            </div>
          </div>
        `;
      };

      const renderTags = (attrib, index) => {
        let row = `<button class="custom-tag" value="${attrib.value}">${attrib.heading}</button>`
        return row;
      };

      const buildCustomRow = ({ customField }) => {
        let row = `
        <div class="gjs-data-frame-block">
          <div class="pt-2" style="display: flex; column-gap: 10px; align-items: center;">
            <div class="gjs-sm-label" data-sm-label="">
              <span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_custom_select_type || `Select Type`} : </span>
            </div>
            <div style="display:flex;">
              <input type="radio" name="customField" value="Text" ${customField?.type !== 'Image' ? 'checked' : ''}/><div>${editorStaticContent?.dataframe_custom_type_text || `Text`}</div>
            </div>
            <div style="display:flex;">
              <input type="radio" name="customField" value="Image" ${customField?.type === 'Image' ? 'checked' : ''}/><div>${editorStaticContent?.dataframe_custom_type_image || `Image`}</div>
            </div>
          </div>
          <div class="menu-item gjs-data-frame-wrapper" style="">
            <div class="gjs-sm-label" data-sm-label="">
              <span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_iterate_label || `Iterate`}</span>
            </div>
            <select class="custom-iterate df-select" name="dataFrameName">
              <option value="None" ${'None' === customField?.iterate && 'selected'}>${editorStaticContent?.dataframe_iterate_none_label || `None`}</option>
              ${
                dataFrameKeys.namings?.map(product => product.dataFrameNames).concat().flat().filter(f => !f.hasNestedDataFrame && !f.metaAttribute).length !== 0
                  ? dataFrameKeys.namings.map(frame => {
                    if(frame.dataFrameNames.filter(f => !f.hasNestedDataFrame && !f.metaAttribute).length > 0) {
                      return `<optgroup label="${frame.productName}">
                        ${frame.dataFrameNames.filter(f => !f.hasNestedDataFrame && !f.metaAttribute).map(f => {
                          return `<option value="${f.key}" data-productname="${frame.productName}" ${f.key === customField?.iterate && 'selected'}>${f.label}</option>`;
                        })}
                        </optgroup>`;
                    }
                    })
                  : `<option>${editorStaticContent?.dataframe_no_options || `No options`}</option>`
              }
            </select>
          </div>
          ${customField.raw ? `<div>
            <div class="gjs-sm-label" data-sm-label="">
              ${editorStaticContent?.dataframe_custom_available_fields_label || `Available Fields`}
            </div>
            <div class="custom-tag-panel">
              ${customField.raw.filter(f => !f.metaAttribute).map((attrib, index) => {
                return renderTags(attrib, index)
              }).join('')}
            </div>
          </div>` : ``}
          <div class="gjs-sm-label" data-sm-label="">
            <span class="gjs-sm-icon" title="">
              ${editorStaticContent?.dataframe_column_name_label || `Column Name`}<span style="color: red;padding-left: 4px;">*</span>
            </span>
          </div>
          <input class="gjs-data-frame-column-heading custom-heading" type="text" value="${customField?.heading ? customField?.heading : ''}" style="width: 100%"/>
          <div class="gjs-sm-label" data-sm-label="">
            ${customField?.type === 'Image' ?
              `<span class="gjs-sm-icon" title="">${editorStaticContent?.dataframe_column_image_label || `Image`}<span style="color: red;padding-left: 4px;">*</span></span>` :
              `<span class="gjs-sm-icon" title="">
                ${editorStaticContent?.dataframe_column_value_label || `Value`}<span style="color: red;padding-left: 4px;">*</span>
              </span>`}
          </div>
          <div>
            ${customField?.type === 'Image' ? `<img id="customImage" src="${customField?.value}" style="width: 150px; ${customField?.value ? '' : 'display: none;'}"/>` : ``}
            ${(customField?.type === 'Image' && customField?.value !== '')
              ? `<div<span style="width: 150px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis">${customField?.imageName}</span>
                  <a class="remove-image-btn" style="display: inline-block; cursor: pointer; color: red; font-size: 12px;">
                    X
                  </a></div` 
              : `<input 
                  class="gjs-data-frame-column-value custom-value" 
                  type="${customField?.type === 'Image' ? `file" id="customFile" name="filename` : `text`}" 
                  value="${customField?.value ? customField.value : ''}" 
                  style="width: 100%"
                />`}
          </div>
          ${customField?.type === 'Image'
            ? `<div style="font-size: 10px;">${
                editorStaticContent?.dataframe_custom_image_note[0] || 'Note: Supports only'
              } ${acceptTypesLabel} ${editorStaticContent?.dataframe_custom_image_note[1] || 'images upto'} ${acceptLimitLabel} ${editorStaticContent?.dataframe_custom_image_note[2] || 'MB in size.'}</div>`
            : ``}
          ${customField?.value === '' || customField?.isDirty ? 
            `<div>
              <a class="add-field-btn" style="display: inline-block; cursor: default; pointer-events: none; color: #aaa; margin-top: 10px; font-size: 12px;">
                ${editorStaticContent?.dataframe_custom_add_field_label || `Add Field`}
              </a>
            </div>` : 
            `<div>
              <a class="add-field-btn" style="display: inline-block; cursor: pointer; color: red; margin-top: 10px; font-size: 12px;">
                ${editorStaticContent?.dataframe_custom_add_field_label || `Add Field`}
              </a>
            </div>`
          }
        </div>
        `;
        return row;
      };

      if (keyName) {
        if (items[keyName]?.length > 0) {
          rows.push('<div class="gjs-data-frame-row">');
          items[keyName].forEach((attrib, index) => {
            rows.push(`${index !== 0 ? '<hr/>' : ''}`);
            let row = fieldsRow(attrib, index, false);
            rows.push(row);
          });
          rows.push('</div>');
        }
        if (items['raw']?.filter(f => !f.metaAttribute)?.length > 0) {
          rows.push('<div class="gjs-data-frame-row">');
          items['raw']?.filter(f => !f.metaAttribute)?.forEach((attrib, index) => {
            rows.push(`${index !== 0 ? '<hr/>' : ''}`);
            let row = fieldsRow(attrib, index, true);
            rows.push(row);
          });
          rows.push('</div>');
        }

        if (items.dataFrameName) {
          rows.push(`<div class="gjs-data-frame-row">`);
          let customRow = buildCustomRow(items);
          rows.push(customRow);
          rows.push(`</div>`);
        }

        el.innerHTML = rows.join('');
        el.querySelectorAll('.add-btn').forEach(elem =>
          elem.removeEventListener('click', handleAddBtn)
        );
        el.querySelectorAll('.add-btn').forEach(elem =>
          elem.addEventListener('click', handleAddBtn.bind(null, items, el))
        );

        el.querySelectorAll('.delete-btn').forEach(elem =>
          elem.removeEventListener('click', handleDeleteBtn)
        );
        el.querySelectorAll('.delete-btn').forEach(elem =>
          elem.addEventListener('click', handleDeleteBtn.bind(null, items, el))
        );

        el.querySelectorAll('.move-up-btn').forEach(elem =>
          elem.removeEventListener('click', handleMoveUpBtn)
        );
        el.querySelectorAll('.move-up-btn').forEach(elem =>
          elem.addEventListener('click', handleMoveUpBtn.bind(null, items, el))
        );
        el.querySelectorAll('.move-down-btn').forEach(elem =>
          elem.removeEventListener('click', handleMoveDownBtn)
        );
        el.querySelectorAll('.move-down-btn').forEach(elem =>
          elem.addEventListener(
            'click',
            handleMoveDownBtn.bind(null, items, el)
          )
        );

        el.querySelectorAll('.data-frame-date-format').forEach(elem =>
          elem.removeEventListener('change', handleDateFormatChange)
        );
        el.querySelectorAll('.data-frame-date-format').forEach(elem =>
          elem.addEventListener('change', handleDateFormatChange.bind(null, el))
        );

        el.querySelectorAll('.gjs-data-frame-column-heading').forEach(elem =>
          elem.removeEventListener('change', handleColumnHeadingChange)
        );
        el.querySelectorAll('.gjs-data-frame-column-heading').forEach(elem =>
          elem.addEventListener(
            'change',
            handleColumnHeadingChange.bind(null, el)
          )
        );

        el.querySelectorAll('.gjs-data-frame-column-value').forEach(elem =>
          elem.removeEventListener('change', handleColumnValueChange)
        );
        el.querySelectorAll('.gjs-data-frame-column-value').forEach(elem =>
          elem.addEventListener(
            'change',
            handleColumnValueChange.bind(null, el)
          )
        );

        el.querySelectorAll('.gjs-data-frame-column-alignment').forEach(elem =>
          elem.removeEventListener('change', handleAlignmentChange)
        );
        el.querySelectorAll('.gjs-data-frame-column-alignment').forEach(elem =>
          elem.addEventListener('change', handleAlignmentChange.bind(null, el))
        );

        el.querySelectorAll('.gjs-data-frame-column-width').forEach(elem =>
          elem.removeEventListener('change', handleColumnWidthChange)
        );
        el.querySelectorAll('.gjs-data-frame-column-width').forEach(elem =>
          elem.addEventListener(
            'change',
            handleColumnWidthChange.bind(null, el)
          )
        );

        el.querySelectorAll('.gjs-data-frame-column-decimal').forEach(elem =>
          elem.removeEventListener('change', handleDecimalValueChange)
        );
        el.querySelectorAll('.gjs-data-frame-column-decimal').forEach(elem =>
          elem.addEventListener(
            'change',
            handleDecimalValueChange.bind(null, el)
          )
        );

        el.querySelectorAll('input[name="customField"]').forEach(elem =>
          elem.removeEventListener('change', handleCustomFieldTypeChange)
        );

        el.querySelectorAll('input[name="customField"]').forEach(elem =>
          elem.addEventListener('change', handleCustomFieldTypeChange.bind(null, el))
        );

        el.querySelectorAll('.custom-iterate').forEach(elem =>
          elem.removeEventListener('change', handleCustomIterateChange)
        );

        el.querySelectorAll('.custom-iterate').forEach(elem =>
          elem.addEventListener('change', handleCustomIterateChange.bind(null, el))
        );

        el.querySelectorAll('.custom-heading').forEach(elem =>
          elem.removeEventListener('change', handleCustomHeadingChange)
        );

        el.querySelectorAll('.custom-heading').forEach(elem =>
          elem.addEventListener('change', handleCustomHeadingChange.bind(null, el))
        );

        el.querySelectorAll('.custom-value').forEach(elem =>
          elem.removeEventListener('change', handleCustomValueChange)
        );

        el.querySelectorAll('.custom-value').forEach(elem =>
          elem.addEventListener('change', handleCustomValueChange.bind(null, el))
        );

        el.querySelectorAll('.remove-image-btn').forEach(elem =>
          elem.removeEventListener('click', handleRemoveImage)
        );

        el.querySelectorAll('.remove-image-btn').forEach(elem =>
          elem.addEventListener('click', handleRemoveImage.bind(null, el))
        );

        el.querySelectorAll('.add-field-btn').forEach(elem =>
          elem.removeEventListener('click', handleAddCustomField)
        );

        el.querySelectorAll('.add-field-btn').forEach(elem =>
          elem.addEventListener('click', handleAddCustomField.bind(null, el))
        );

        el.querySelectorAll('.custom-tag').forEach(elem =>
          elem.removeEventListener('click', handleCustomTagClick)
        );

        el.querySelectorAll('.custom-tag').forEach(elem =>
          elem.addEventListener('click', handleCustomTagClick.bind(null, el))
        );
      } else {
        el.innerHTML = '';
      }
    };
  };

  const barcodePlugin = (editor, options) => {
    const getBcEmptyContent = () => {
      return `
      <div data-gjs-type="barcode-component" data-class="barcode" style="padding: 15px; border: 2px dashed #CCC; background-color: #EEE; color: #999; text-align: center;">
        ${editorStaticContent?.barcode_empty_content || `BARCODE`}
      </div>
      `;
    };

    editor.BlockManager.add('barcode-block', {
      label: editorStaticContent?.barcode_empty_content || 'BARCODE',
      category: 'Advanced',
      attributes: { class: 'fa fa-image' },
      content: getBcEmptyContent()
    });

    editor.DomComponents.addType('barcode-component', {
      isComponent: el => {
        if (el.dataset?.class === 'barcode') {
          const result = { type: 'barcode-component', item: {} };
          let extractedFields = {};
          let content = el.innerHTML;
          //Reverse Parsing dataFrame
          let matchedContent = content?.match(/{{!--(barcodeField)(.*)--}}/g);
          if (matchedContent?.length) {
            let barcodeFieldRegExp = /{{!--barcodeField(.*)--}}/;
            let matchedBarcodeFields = barcodeFieldRegExp.exec(matchedContent);
            if (matchedBarcodeFields?.length >= 2) {
              try {
                extractedFields = JSON.parse(matchedBarcodeFields[1]);
              } catch (error) {
                console.error('Error parsing barcode field JSON, removing corrupted element:', error);
                // Return false to remove the corrupted element
                return false;
              }
            }
            result['item'] = extractedFields.props || {};
          }
          return result;
        }
      },
      model: {
        defaults: {
          testprop: 1,
          item: {},
          droppable: false,
          tableStyles: {
            borderColor: '#ece9e9',
            headerFontColor: '#000',
            headerFontSize: '14',
            textAlign: 'center',
            cellPadding: '8',
            valueFontColor: '#000',
            valueFontSize: '12'
          }
        },
        styles: ``,
        init() {
          this.listenTo(this, 'change:item', this.handlePropChange);
        },
        removed() {},
        handlePropChange() {
          updateCanvas(this.get('item'));
        }
      },
      view: {
        init() {},
        onRender() {
          let updatedItems = this.model.get('item');
          if (Object.keys(updatedItems).length > 0 && updatedItems.key) {
            setTimeout(() => {
              updateCanvas(updatedItems, this.model);
            }, 100);
          }
        }
      }
    });

    editor.StyleManager.addType('barcode-property', {
      // Create UI
      create({ props, change }) {
        let getSelected = editor.getSelected();
        let selectedComponentItem;
        if (getSelected?.get('type') === 'barcode-component') {
          selectedComponentItem = getSelected.get('item');
        }

        const el = document.createElement('div');
        el.innerHTML = `
        <div class="menu-item gjs-barcode-wrapper" style="">
          <select class="barcode-name" name="barcodeName">
            <option value="" selected disabled hidden>${editorStaticContent?.barcode_select_template_key || `Select template key`}</option>
            ${
              props.templateKeys?.length !== 0
                ? props.templateKeys.map(frame => {
                  if(frame?.schemaConditionAttributes?.filter(f => Object.keys(f).length > 0 && !f.metaAttribute).length > 0) {
                    return `<optgroup label="${frame.productName}">
                      ${frame?.schemaConditionAttributes?.filter(f => Object.keys(f).length > 0 && !f.metaAttribute).map(f => {
                        return `<option value="${f.text}" data-productname="${
                          frame.productName
                        }"
                          ${f.text === selectedComponentItem?.handleBar &&
                            'selected'}>${f.name}</option>`;
                      })}
                      </optgroup>`;
                  } else {
                    return ``;
                  }
                  })
                : `<option>${editorStaticContent?.barcode_no_options || `No options`}</option>`
            }  
          </select>
          <div class="gjs-barcode-rows"></div>
        <div>
        `;
        const selectEl = el.querySelector('.barcode-name');
        selectEl.addEventListener('change', event => change({ event })); // `change` will trigger the emit
        return el;
      },

      // Propagate UI changes up to the targets
      emit({ props, updateStyle }, { event, partial }) {
        const el = event.target;
        const { value } = el;
        let productName = event.target.selectedOptions[0]?.dataset?.productname;
        let templateKey = (props.templateKeys.filter(
          data => data.productName === productName
        ) || [])[0]?.schemaConditionAttributes?.filter(d => {
          return d.text === value;
        })[0];
        let getSelected = editor.getSelected();
        let updatedItems = {};
        if (getSelected?.get('type') === 'barcode-component') {
          let selectedComponentItem = getSelected.get('item');
          if (
            selectedComponentItem?.key &&
            selectedComponentItem.hasOwnProperty(value)
          ) {
            updatedItems = selectedComponentItem;
          }
        }
        if (Object.keys(updatedItems).length === 0) {
          updatedItems = { handleBar: templateKey.text, key: templateKey.key };
        }

        let rowsElement = el.parentElement.querySelector('.gjs-barcode-rows');
        buildBcMenuRows(updatedItems, rowsElement);
        updateDfItem(updatedItems);
      },
      // Update UI (eg. when the target is changed)
      update({ value, el }) {
        let getSelected = editor.getSelected();
        if (getSelected?.get('type') === 'barcode-component') {
          let updatedItems = getSelected.get('item');
          if (updatedItems.isHeightDirty || updatedItems.isWidthDirty) {
            updatedItems.isOpen = true;
          } else {
            delete updatedItems.isOpen;
          }
          let rowsElement = el.querySelector('.gjs-barcode-rows');
          if (updatedItems?.key) {
            el.querySelector('.barcode-name').value = updatedItems?.handleBar;
            buildBcMenuRows(updatedItems, rowsElement);
          } else {
            el.querySelector('.barcode-name').value = '';
            buildBcMenuRows({}, rowsElement);
          }
        }
      },
      // Clean the memory from side effects if necessary (eg. global event listeners, etc.)
      destroy() {}
    });

    editor.on('component:selected', selComponent => {
      const styleManager = editor.StyleManager;
      let type = selComponent.get('type');
      if (type === 'barcode-component') {
        // Bug 657594: Need to remove unused typography component in Dataframe and barcode classes
        selComponent.set({
          unstylable: ['font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align', 'text-shadow']
        });
        styleManager.addSector(
          'bcSector',
          {
            name: editorStaticContent?.barcode_sector_name || 'Barcode',
            properties: [
              {
                type: 'barcode-property',
                property: 'barcode-name',
                label: editorStaticContent?.barcode_sector_name || 'Barcode',
                templateKeys: templateKeys() || []
              }
            ]
          },
          { at: 0 }
        );
        let bcSector = styleManager.getSector('bcSector');
        bcSector.set('open', true);

        setTimeout(() => {
          let bcSectorProps = styleManager.getProperties('bcSector');
          let bcProperty = bcSectorProps.models.filter(
            prop => prop.get('type') === 'barcode-property'
          )[0];

          bcProperty.trigger('change:value');
        }, 250);
      } else {
        styleManager.removeSector('bcSector');
      }
    });

    editor.on('load', () => {
      editor.on('component:add', component => {
        if (component.get('type') === 'barcode-component') {
          let updatedValidation = {};
          updatedValidation[component.cid] = {isDisabled: true};
          if (
            component.attributes.item.isHeightDirty ||
            component.attributes.item.isWidthDirty
          ) {
            updatedValidation[component.cid] = {isDisabled: true};
          }
          setIsValidationError(prevState => ({
            ...prevState,
            ...updatedValidation
          }));
        }
      });
      editor.on('component:remove', component => {
        if (component.get('type') === 'barcode-component') {
          setIsValidationError(prevState => {
            delete prevState[component.cid];
            return { ...prevState };
          });
        }
      });
      editor.on('component:update', component => {
        if (component.get('type') === 'barcode-component') {
          if (
            component.attributes.item.isHeightDirty ||
            component.attributes.item.isWidthDirty
          ) {
            let updatedValidation = {};
            updatedValidation[component.cid] = {isDisabled: true};
            setIsValidationError(prevState => ({
              ...prevState,
              ...updatedValidation
            }));
          } else {
            let updatedValidation = {};
            updatedValidation[component.cid] = {isDisabled: false};
            setIsValidationError(prevState => ({
              ...prevState,
              ...updatedValidation
            }));
          }
        }
      });
    });

    const updateCanvas = (item, component) => {
      let selectedComponent = component ? component : editor.getSelected();

      if (!item.key || item.key?.length === 0) {
        selectedComponent.components('BARCODE');
        selectedComponent.view.el.innerHTML = editorStaticContent?.barcode_empty_content || 'BARCODE';
      } else if (options.isLoading) {
        selectedComponent.components('BARCODE');
        selectedComponent.view.el.innerHTML = editorStaticContent?.barcode_loading || 'Loading...';
      } else {
        // Sent to api
        selectedComponent.components(`
          <div>
            {{!--barcodeField${JSON.stringify({
              props: item
            })}--}}
            <img 
              src="{{barCode ${item.key}}}" 
              width="${item.width ? item.width : 'auto'}"
              height="${item.height ? item.height : 'auto'}"
            >
          </div>`);

        //To display in context
        selectedComponent.view.el.innerHTML = `<div
          width="${item.width ? item.width : 'auto'}"
          height="${item.height ? item.height : 'auto'}"
          style="text-overflow: ellipsis; overflow: hidden;"
        >
          ${item.handleBar}
        </div>`;
      }
    };

    const handleEditBtn = (items, el, event) => {
      let prev = Object.assign({}, items);
      if (prev.isOpen) {
        delete prev.isOpen;
      } else {
        prev.isOpen = true;
      }
      buildBcMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleBarcodeHeightChange = (el, event) => {
      let items = editor.getSelected().get('item');
      let prev = Object.assign({}, items);
      prev.height = escapeHTMLAttributeValue(event.target.value);
      if (prev.height !== '' && Math.sign(prev.height) !== 1) {
        prev.isHeightDirty = true;
      } else if (prev?.isHeightDirty) {
        delete prev.isHeightDirty;
      }
      buildBcMenuRows(prev, el);
      updateDfItem(prev);
    };

    const handleBarcodeWidthChange = (el, event) => {
      let items = editor.getSelected().get('item');
      let prev = Object.assign({}, items);
      prev.width = escapeHTMLAttributeValue(event.target.value);
      if (prev.width !== '' && Math.sign(prev.width) !== 1) {
        prev.isWidthDirty = true;
      } else if (prev?.isWidthDirty) {
        delete prev.isWidthDirty;
      }
      buildBcMenuRows(prev, el);
      updateDfItem(prev);
    };

    const updateDfItem = updatedItems => {
      let selectedComponent = editor.getSelected();
      selectedComponent.set('item', updatedItems);
      selectedComponent.trigger('change:item');
    };

    const buildBcMenuRows = (items, el) => {
      let keyName = Object.keys(items).length > 0 && items.key;
      const fieldsProperties = () => {
        return `
          <div class="gjs-barcode-row">
            <div class="gjs-barcode-properties" style="display:${
              items.isOpen ? `block` : `none`
            }">
              <div>
                <div class="gjs-sm-label" data-sm-label="">
                  <span class="gjs-sm-icon" title="" style="display: flex; column-gap: 4px">
                    ${
                      items?.isHeightDirty === true
                        ? `${editorStaticContent?.barcode_height || `Barcode Height (in px)`}<span style="color: red">${editorStaticContent?.barcode_integer_error || `Should contain only positive integers`}</span>`
                        : `${editorStaticContent?.barcode_height || `Barcode Height (in px)`}`
                    }
                  </span>
                </div>
                <input class="gjs-barcode-height" value="${items?.height ||
                  ''}" style="width: 100%"/>
              </div>
              <div>
                <div class="gjs-sm-label" data-sm-label="">
                  <span class="gjs-sm-icon" title="" style="display: flex; column-gap: 4px">
                    ${
                      items?.isWidthDirty === true
                        ? `${editorStaticContent?.barcode_width || `Barcode Width (in px)`}<span style="color: red">${editorStaticContent?.barcode_integer_error || `Should contain only positive integers`}</span>`
                        : `${editorStaticContent?.barcode_width || `Barcode Width (in px)`}`
                    }
                  </span>
                </div>
                <input class="gjs-barcode-width" value="${items?.width ||
                  ''}" style="width: 100%"/>
              </div>
            </div>
            <a 
              class="edit-btn" style="display: inline-block; cursor: pointer; color: red; 
              pointer-events: all; margin-top: ${
                items.isOpen ? '10px' : '0px'
              }; font-size: 12px;">
              ${items.isOpen ? `${editorStaticContent?.barcode_hide || `Hide`}` : `${editorStaticContent?.barcode_edit || `Edit`}`} ${editorStaticContent?.barcode_properties || `Properties`}
            </a>
          </div>
        `;
      };

      if (keyName) {
        let row = fieldsProperties();
        el.innerHTML = row;

        el.querySelectorAll('.edit-btn').forEach(elem =>
          elem.removeEventListener('click', handleEditBtn)
        );
        el.querySelectorAll('.edit-btn').forEach(elem =>
          elem.addEventListener('click', handleEditBtn.bind(null, items, el))
        );

        el.querySelectorAll('.gjs-barcode-height').forEach(elem =>
          elem.removeEventListener('change', handleBarcodeHeightChange)
        );
        el.querySelectorAll('.gjs-barcode-height').forEach(elem =>
          elem.addEventListener(
            'change',
            handleBarcodeHeightChange.bind(null, el)
          )
        );

        el.querySelectorAll('.gjs-barcode-width').forEach(elem =>
          elem.removeEventListener('change', handleBarcodeWidthChange)
        );
        el.querySelectorAll('.gjs-barcode-width').forEach(elem =>
          elem.addEventListener(
            'change',
            handleBarcodeWidthChange.bind(null, el)
          )
        );
      } else {
        el.innerHTML = '';
      }
    };
  };

  const iterationPlugin = (editor, options) => {
    const getIterationEmptyContent = () => {
      return `
        <div data-gjs-type="iteration-component" data-class="iteration"  style="background-color: #EEE; color: #999; text-align: center;">
          ${editorStaticContent?.iteration_empty_content || `ITERATION`}
        </div>
      `;
    };

    editor.BlockManager.add('iteration-block', {
      label: editorStaticContent?.iteration_empty_content || 'ITERATION',
      category: 'Advanced',
      attributes: { class: 'fa fa-refresh' },
      content: getIterationEmptyContent()
    });

    editor.DomComponents.addType('iteration-component', {
      isComponent: el => {
        if (el.dataset?.class === 'iteration') {
          const result = { type: 'iteration-component', item: {} };
          let extractedFields = {};
          let content = el.innerHTML;
          //Reverse Parsing iteration component
          let matchedContent = content?.match(/{{!--(iterationField)(.*)--}}/g);
          if (matchedContent?.length) {
            let iterationFieldRegExp = /{{!--iterationField(.*)--}}/;
            let matchedIterationFields = iterationFieldRegExp.exec(matchedContent);
            if (matchedIterationFields?.length >= 2) {
              try {
                extractedFields = JSON.parse(matchedIterationFields[1]);
              } catch (error) {
                console.error('Error parsing iteration field JSON, removing corrupted element:', error);
                // Return false to remove the corrupted element
                return false;
              }
            }
            if (extractedFields.props && !extractedFields.props.iterationType) {
              result['item'] = { ...extractedFields.props, iterationType: 'startIteration' };
            } else {
              result['item'] = extractedFields.props || [];
            }
          }
          return result;
        }
      },
      model: {
        defaults: {
          item: {},
          droppable: false
        },
        styles: ``,
        init() {
          this.listenTo(this, 'change:item', this.handlePropChange);
        },
        removed() {},
        handlePropChange() {
          updateCanvas(this.get('item'));
        }
      },
      view: {
        init() {},
        onRender() {
          let updatedItems = this.model.get('item');
          if (Object.keys(updatedItems).length > 0 && updatedItems.key) {
            setTimeout(() => {
              updateCanvas(updatedItems, this.model);
            }, 100);
          }
        }
      }
    });

    editor.StyleManager.addType('iteration-property', {

      // Create UI
      create({ props, change }) {
        let getSelected = editor.getSelected();
        let selectedComponentItem;
        if (getSelected?.get('type') === 'iteration-component') {
          selectedComponentItem = getSelected.get('item');
        }

        const el = document.createElement('div');
        el.innerHTML = `
        <div class="menu-item gjs-iteration-wrapper" style="">
          <select class="iteration-name" name="iterationName">
            <option value="" selected disabled hidden>${editorStaticContent?.iteration_select_dataframe_label || `Select Data frame name`}</option>
            ${
              props.frames?.length !== 0
                ? props.frames.map(frame => {
                  if(frame?.dataFrameNames?.filter(f => Object.keys(f).length > 0 && f.hasNestedDataFrame).length > 0) {
                    return `<optgroup label="${frame.productName}">
                      ${frame?.dataFrameNames?.filter(f => Object.keys(f).length > 0 && f.hasNestedDataFrame).sort((a, b) => a?.label.localeCompare(b?.label)).map(f => {
                        return `<option value="${f.label}" data-productname="${frame.productName}">${f.label}</option>`;
                      })}
                      </optgroup>`;
                  }
                  })
                : `<option>${editorStaticContent?.iteration_no_options || `No options`}</option>`
            }
          </select>
          <div class="gjs-iteration-rows"></div>
        </div>
        `;
        const selectEl = el.querySelector('.iteration-name');
        selectEl.addEventListener('change', event => change({ event })); // `change` will trigger the emit
        return el;
      },

      // Propagate UI changes up to the targets
      emit({ props, updateStyle }, { event, partial }) {
        const el = event.target;
        const { value } = el;
        let productName = event.target.selectedOptions[0]?.dataset?.productname;
        let dataField = (props.dataFields.filter(
          data => data.productName === productName
        ) || [])[0]?.dataFrames?.filter(d => {
          return d.name === value;
        })[0];
        let keyName = dataField.name;
        let getSelected = editor.getSelected();
        let updatedItems = {};
        if (getSelected?.get('type') === 'iteration-component') {
          let selectedComponentItem = getSelected.get('item');
          if (
            selectedComponentItem?.dataFrameName &&
            selectedComponentItem.hasOwnProperty(value)
          ) {
            updatedItems = selectedComponentItem;
          }
        }
        if (Object.keys(updatedItems).length === 0) {
          updatedItems = {
            dataFrameName: keyName,
            key: dataField.key,
            productName: productName,
            iterationType: 'startIteration'
          }
        }

        let rowsElement = el.parentElement.querySelector('.gjs-iteration-rows');
        buildIterationMenuRows(updatedItems, rowsElement);
        updateDfItem(updatedItems);
      },

      // Update UI (eg. when the target is changed)
      update({ value, el }) {
        let getSelected = editor.getSelected();
        if (getSelected?.get('type') === 'iteration-component') {
          let updatedItems = getSelected.get('item');
          let rowsElement = el.querySelector('.gjs-iteration-rows');
          if (updatedItems?.dataFrameName) {
            el.querySelector('.iteration-name').value = updatedItems?.dataFrameName;
            buildIterationMenuRows(updatedItems, rowsElement);
          } else {
            el.querySelector('.iteration-name').value = '';
            buildIterationMenuRows({}, rowsElement);
          }
        }
      },

      // Clean the memory from side effects if necessary (eg. global event listeners, etc.)
      destroy() {}
    });

    editor.on('component:selected', selComponent => {
      const styleManager = editor.StyleManager;
      let type = selComponent.get('type');
      if (type === 'iteration-component') {
        selComponent.set({
          stylable: ['iteration-name']
        });
        styleManager.addSector(
          'iterationSector',
          {
            name: editorStaticContent?.iteration_sector_name || 'Iteration',
            properties: [
              {
                type: 'iteration-property',
                property: 'iteration-name',
                label: editorStaticContent?.iteration_sector_name || 'Iteration',
                frames: dataFrameKeys.namings || [],
                dataFields: dataFrameKeys.detailedList || [],
                dateFormatMenuOptions: dateFormatMenuOptions || []
              }
            ]
          },
          { at: 0 }
        );
        let iterationSector = styleManager.getSector('iterationSector');
        iterationSector.set('open', true);

        setTimeout(() => {
          let iterationSectorProps = styleManager.getProperties('iterationSector');
          let iterationProperty = iterationSectorProps.models.filter(
            prop => prop.get('type') === 'iteration-property'
          )[0];

          iterationProperty.trigger('change:value');
        }, 250);
      } else {
        styleManager.removeSector('iterationSector');
      }
    });

    editor.on('load', () => {
      editor.on('component:add', component => {
        if (component.get('type') === 'iteration-component') {
          let updatedValidation = {};
          updatedValidation[component.cid] = {isDisabled: false};
          if (
            !component.attributes.item.dataFrameName &&
            !component.attributes.item.iterationType
          ) {
            updatedValidation[component.cid] = {isDisabled: true};
          }
          setIsValidationError(prevState => ({
            ...prevState,
            ...updatedValidation
          }));
        }
      });
      editor.on('component:remove', component => {
        if (component.get('type') === 'iteration-component') {
          setIsValidationError(prevState => {
            delete prevState[component.cid];
            return { ...prevState };
          });
        }
      });
      editor.on('component:update', component => {
        if (component.get('type') === 'iteration-component') {
          if (
            !component.attributes.item.dataFrameName &&
            !component.attributes.item.iterationType
          ) {
            let updatedValidation = {};
            updatedValidation[component.cid] = {isDisabled: true};
            setIsValidationError(prevState => ({
              ...prevState,
              ...updatedValidation
            }));
          } else {
            let updatedValidation = {};
            updatedValidation[component.cid] = {isDisabled: false};
            setIsValidationError(prevState => ({
              ...prevState,
              ...updatedValidation
            }));
          }
        }
      });
    });

    const updateCanvas = (item, component) => {
      let selectedComponent = component ? component : editor.getSelected();
      let keyName = item.dataFrameName;

      if (!keyName) {
        selectedComponent.components('ITERATION');
        selectedComponent.view.el.innerHTML = editorStaticContent?.iteration_empty_content || 'ITERATION';
      } else if (options.isLoading) {
        selectedComponent.components('ITERATION');
        selectedComponent.view.el.innerHTML = editorStaticContent?.iteration_loading || 'Loading...';
      } else {
        // Sent to api
        selectedComponent.components(`
          <div>
            {{!--iterationField${JSON.stringify({
              props: item
            })}--}}
            <span class="jsx-syntax">${item.iterationType ? `${item.iterationType === "endIteration" ? `{{/each}}` : `{{#each ${item.key}}}`}` : `{{#each ${item.key}}}`}</span>
          </div>`);
        //To display in context
        selectedComponent.view.el.innerHTML = `<div style="text-overflow: ellipsis; overflow: hidden;">
          ${item.iterationType === "endIteration" ? `${editorStaticContent?.iteration_end_label || `END`}` : `${editorStaticContent?.iteration_begin_label || `BEGIN`}`} - {{${item.key}}}
        </div>`;
      }
    }

    const handleIterationTypeChange = (items, el, event) => {
      let prev = Object.assign({}, items);
      prev.iterationType = event.target.value;
      buildIterationMenuRows(prev, el);
      updateDfItem(prev);
    };

    const updateDfItem = updatedItems => {
      let selectedComponent = editor.getSelected();
      selectedComponent.set('item', updatedItems);
      selectedComponent.trigger('change:item');
    };

    const buildIterationMenuRows = (items, el) => {
      let keyName = Object.keys(items).length > 0 && items.key;
      const fieldsProperties = () => {
        return `<div class="gjs-iteration-row">
          <div>
            <label>${editorStaticContent?.iteration_select_type || `Select Type`} : </label>
          </div>
          <div style="display:flex; margin-bottom:0.5rem" class="pl-2">
            <input style="width: auto;" class="gjs-iteration-type pr-2" type="radio" name="iterationType" value="startIteration" ${items.iterationType === 'startIteration' ? 'checked' : ''}/>
            <div class="pl-2">${editorStaticContent?.iteration_type_start_iteration || `Start Iteration`}</div>
          </div>
          <div style="display:flex; margin-bottom:0.5rem" class="pl-2">
            <input style="width: auto;" class="gjs-iteration-type pr-2" type="radio" name="iterationType" value="endIteration" ${items.iterationType === 'endIteration' ? 'checked' : ''}/>
            <div class="pl-2">${editorStaticContent?.iteration_type_end_iteration || `End Iteration`}</div>
          </div>
        </div>`;
      };

      if (keyName) {
        let row = fieldsProperties();
        el.innerHTML = row;

        el.querySelectorAll('.gjs-iteration-type').forEach(elem => {
          elem.removeEventListener('change', handleIterationTypeChange)
        }
        );
        el.querySelectorAll('.gjs-iteration-type').forEach(elem => {
          elem.addEventListener('change', handleIterationTypeChange.bind(null, items, el))
        }
        );
      } else {
        el.innerHTML = '';
      }
    };
  };

  const htmlPlugin = (editor, options) => {
    const getHtmlEmptyContent = () => {
      return `
        <div data-gjs-type="html-component" data-class="htmlcomponent" style="color: #999; text-align: center;">
          ${editorStaticContent?.html_empty_content || `HTML`}
        </div>
      `;
    };

    editor.BlockManager.add('html-block', {
      label: editorStaticContent?.html_empty_content || 'HTML',
      category: 'Advanced',
      attributes: { class: 'fa fa-code' },
      content: getHtmlEmptyContent()
    });

    editor.DomComponents.addType('html-component', {
      isComponent: el => {
        if (el.dataset?.class === 'htmlcomponent') {
          const result = { type: 'html-component', item: {} };
          let extractedFields = {};
          let content = el.innerHTML;
          //Reverse Parsing html component
          let matchedContent = content?.match(/{{!--(htmlField)(.*)--}}/g);
          if (matchedContent?.length) {
            let htmlFieldRegExp = /{{!--htmlField(.*)--}}/;
            let matchedHtmlFields = htmlFieldRegExp.exec(matchedContent);
            if (matchedHtmlFields?.length >= 2) {
              try {
                extractedFields = JSON.parse(matchedHtmlFields[1]);
              } catch (error) {
                console.error('Error parsing HTML field JSON, removing corrupted element:', error);
                // Return false to remove the corrupted element
                return false;
              }
            }
            if (extractedFields.props) {
              try {
                let decodedItem = JSON.parse(Base64.decode(extractedFields.props));
                result['item'] = { ...decodedItem } || {};
              } catch (error) {
                console.error('Error parsing Base64 decoded JSON, removing corrupted element:', error);
                // Return false to remove the corrupted element
                return false;
              }
            }
          }
          return result;
        }
      },
      model: {
        defaults: {
          item: {},
          droppable: false,
          editable: false,
          locked: true,
          highlightable: false
        },
        styles: ``,
        init() {
          this.listenTo(this, 'change:item', this.handlePropChange);
        },
        removed() {},
        handlePropChange() {
          updateCanvas(this.get('item'));
        }
      },
      view: {
        init() {},
        onRender() {
          let updatedItems = this.model.get('item');
          if (Object.keys(updatedItems).length > 0 && updatedItems.content) {
            setTimeout(() => {
              updateCanvas(updatedItems, this.model);
            }, 100);
          }
        }
      }
    });

    editor.StyleManager.addType('html-property', {
      // Create UI
      create({ props, change }) {
        let getSelected = editor.getSelected();
        let selectedComponentItem;
        if (getSelected?.get('type') === 'html-component') {
          selectedComponentItem = getSelected.get('item');
        }

        const el = document.createElement('div');
        el.innerHTML = `
        <div class="menu-item gjs-html-wrapper" style="">
          <textarea class="html-content" name="htmlContent" placeholder="${editorStaticContent?.html_placeholder || `Enter HTML content`}">${selectedComponentItem?.content || ''}</textarea>
        </div>
        `;
        const textareaEl = el.querySelector('.html-content');
        textareaEl.addEventListener('input', event => change({ event })); // `change` will trigger the emit
        return el;
      },

      // Propagate UI changes up to the targets
      emit({ props, updateStyle }, { event, partial }) {
        const el = event.target;
        const { value } = el;
        let getSelected = editor.getSelected();
        let updatedItems = {};
        if (getSelected?.get('type') === 'html-component') {
          let selectedComponentItem = getSelected.get('item');
          updatedItems = selectedComponentItem;
        }
        updatedItems.content = value;

        let selectedComponent = editor.getSelected();
        selectedComponent.set('item', updatedItems);
        selectedComponent.trigger('change:item');
        // Instead of editor.on('component:update', component => {});
        // Used the below logic to update validation
        if (!selectedComponent.attributes?.item?.content) {
          let updatedValidation = {};
          updatedValidation[selectedComponent.cid] = {isDisabled: true};
          setIsValidationError(prevState => ({
            ...prevState,
            ...updatedValidation
          }));
        } else {
          let updatedValidation = {};
          updatedValidation[selectedComponent.cid] = {isDisabled: false};
          setIsValidationError(prevState => ({
            ...prevState,
            ...updatedValidation
          }));
        }
      },

      // Update UI (eg. when the target is changed)
      update({ value, el }) {
        let getSelected = editor.getSelected();
        if (getSelected?.get('type') === 'html-component') {
          let updatedItems = getSelected.get('item');
          el.querySelector('.html-content').value = updatedItems?.content || '';
        }
      },

      // Clean the memory from side effects if necessary (eg. global event listeners, etc.)
      destroy() {}
    });

    editor.on('component:selected', selComponent => {
      const styleManager = editor.StyleManager;
      let type = selComponent.get('type');
      if (type === 'html-component') {
        styleManager.addSector(
          'htmlSector',
          {
            name: editorStaticContent?.html_sector_name || 'HTML',
            properties: [
              {
                type: 'html-property',
                property: 'html-content',
                label: editorStaticContent?.html_content_label || 'HTML Content'
              }
            ]
          },
          { at: 0 }
        );
        let htmlSector = styleManager.getSector('htmlSector');
        htmlSector.set('open', true);

        setTimeout(() => {
          let htmlSectorProps = styleManager.getProperties('htmlSector');
          let htmlProperty = htmlSectorProps.models.filter(
            prop => prop.get('type') === 'html-property'
          )[0];

          htmlProperty.trigger('change:value');
        }, 250);
      } else {
        styleManager.removeSector('htmlSector');
      }
    });

    editor.on('load', () => {
        // Add this event listener to handle new HTML components being added
      editor.on('component:add', component => {
        if (component.get('type') === 'html-component') {
          // Ensure new HTML components always start with an empty state
          if (!component.get('item')?.content) {
            component.set('item', { content: '' });
            component.trigger('change:item');
          }
          let updatedValidation = {};
          updatedValidation[component.cid] = {isDisabled: false};
          if (!component.attributes.item.content) {
            updatedValidation[component.cid] = {isDisabled: true};
          }
          setIsValidationError(prevState => ({
            ...prevState,
            ...updatedValidation
          }));
        }
      });
      editor.on('component:remove', component => {
        if (component.get('type') === 'html-component') {
          setIsValidationError(prevState => {
            delete prevState[component.cid];
            return { ...prevState };
          });
        }
      });
    });

    const updateCanvas = (item, component) => {
      let selectedComponent = component ? component : editor.getSelected();

      // Sanitize the content in the item object at the beginning
      const sanitizedItem = {
        ...item,
        content: item.content ? sanitizeHtml(item.content) : item.content
      };
      let content = sanitizedItem.content;

      if (selectedComponent) {
        if (!content || content.length === 0) {
          selectedComponent.components('HTML');
          selectedComponent.view.el.innerHTML = editorStaticContent?.html_empty_content || 'HTML';
        } else if (options.isLoading) {
          selectedComponent.components('HTML');
          selectedComponent.view.el.innerHTML = editorStaticContent?.html_loading || 'Loading...';
        } else {
          // Sent to api
          selectedComponent.components(`
            <div>
              {{!--htmlField${JSON.stringify({
                props: Base64.encode(JSON.stringify(sanitizedItem))
              })}--}}
              <div>${content}</div>
            </div>`);
  
          //To display in context
          const parsedDoc = new DOMParser().parseFromString(content, 'text/html');
          selectedComponent.view.el.replaceChildren(...Array.from(parsedDoc.body.childNodes));
        }
      }
    }

    const handleHtmlContentChange = (el, event) => {
      let items = editor.getSelected().get('item');
      let prev = Object.assign({}, items);
      prev.content = escapeHTMLAttributeValue(event.target.value);
      buildHtmlMenuRows(prev, el);
      updateDfItem(prev);
    }

    const updateDfItem = updatedItems => {
      let selectedComponent = editor.getSelected();
      selectedComponent.set('item', updatedItems);
      selectedComponent.trigger('change:item');
    }

    const buildHtmlMenuRows = (items, el) => {
      let keyName = Object.keys(items).length > 0 && items.key;
      const fieldsProperties = () => {
        return `<div class="gjs-html-row">
          <textarea class="gjs-html-content" style="width: 100%; height: 100%;">${items.content || ''}</textarea>
        </div>`;
      };

      if (keyName) {
        let row = fieldsProperties();
        el.innerHTML = row;

        el.querySelectorAll('.gjs-html-content').forEach(elem =>
          elem.removeEventListener('input', handleHtmlContentChange)
        );
        el.querySelectorAll('.gjs-html-content').forEach(elem =>
          elem.addEventListener('input', handleHtmlContentChange.bind(null, el))
        );
      } else {
        el.innerHTML = '';
      }
    }
  };

  const languagePlugin = (editor, options) => {
    if (editorStaticContent) {
      editor.I18n.addMessages({
        [currentTemplateLanguage?.grapesLocale]: editorStaticContent
      });
    }
  };

  const fetchGrapesLocaleContent = async () => {
    try {
      const localeContent = await import(`grapesjs/locale/${currentTemplateLanguage?.grapesLocale}.js`);
      setGrapesLocaleContent(localeContent.default);
    } catch (error) {
      setGrapesLocaleContent(null);
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGrapesLocaleContent();
  }, [currentTemplateLanguage]);

  useEffect(() => {
    setEditor(grapesjs.init({
      container: '#gjs',
      noticeOnUnload: false,
      plugins: ['gjs-preset-webpage', dataFramePlugin, barcodePlugin, iterationPlugin, htmlPlugin, languagePlugin],
      styleManager: { clearProperties: true, sectors: [] },
      colorPicker: { appendTo: 'parent', offset: { top: 26, left: -166 } },
      i18n: {
        locale: currentTemplateLanguage?.grapesLocale,
        detectLocale: false,
        messages: {
          [currentTemplateLanguage?.grapesLocale]: grapesLocaleContent
        }
      },
      pluginsOpts: {
        [dataFramePlugin]: {
          frames: dataFrameKeys.namings || [],
          dataFields: dataFrameKeys.detailedList || [],
          dateFormatMenuOptions: dateFormatMenuOptions || [],
          isLoading: isLoading
        },
        [barcodePlugin]: {
          templateKeys: templateKeys() || [],
          isLoading: isLoading
        },
        [iterationPlugin]: {
          isLoading: isLoading
        }
      },
      storageManager: false
    }));
  }, [dataFrameKeys, grapesLocaleContent, editorStaticContent])

  useEffect(() => {
    if (editor) {
      let isHydratingEditorContent = false;

      if (!editor.__openAssetsGuardInstalled) {
        const openAssetsCommand = editor.Commands.get('open-assets');
        if (openAssetsCommand) {
          // Block built-in asset modal by default; custom image panel handles updates.
          editor.Commands.add('open-assets', {
            run(ed, sender, opts = {}) {
              if (opts?.allowOpenAssets === true && typeof openAssetsCommand.run === 'function') {
                return openAssetsCommand.run.call(openAssetsCommand, ed, sender, opts);
              }
              return undefined;
            },
            stop(ed, sender, opts = {}) {
              if (typeof openAssetsCommand.stop === 'function') {
                try {
                  return openAssetsCommand.stop.call(openAssetsCommand, ed, sender, opts);
                } catch (err) {
                  // Silently handle errors during stop (modal may not be initialized)
                  console.debug('Error stopping openAssets command:', err);
                  return undefined;
                }
              }
              return undefined;
            }
          });
        }
        editor.__openAssetsGuardInstalled = true;
      }

      // Prevent built-in image asset modal from auto-opening when dropping image blocks.
      const imageType = editor.DomComponents.getType('image');
      if (imageType?.model?.prototype?.defaults) {
        imageType.model.prototype.defaults = {
          ...imageType.model.prototype.defaults,
          activate: false
        };
      }

      // Removing unwanted (as of May 2023) components from UI
      editor.BlockManager.remove([
        'video',
        'map',
        'h-navbar',
        'countdown',
        'link',
        'link-block',
        'quote',
        'text-basic',
        ...(!hasNestedDataFrame ? ['iteration-block'] : [])
      ]);

      editor.on('asset:add', props => {
        if (props.attributes.type !== 'image') {
          setTimeout(() => {
            document.querySelectorAll('.gjs-am-close')[0].click();
            openSnackBarError(
              'error',
              `${props.attributes.name} is not an image`
            );
          }, 1000);
        }
      });

      editor.on('component:add', component => {
        if (component.get('type') === 'image') {
          if (!isHydratingEditorContent) {
            const existingStyles = component.getStyle?.() || {};
            const existingAttributes = component.getAttributes?.() || {};
            const existingWidthMode = component.get('imageWidthMode') || existingAttributes['data-image-width-mode'];
            const hasExistingSizing = Boolean(
              existingStyles.width ||
              existingStyles.maxWidth ||
              existingStyles.display ||
              existingStyles.float ||
              existingStyles.margin ||
              existingWidthMode
            );

            // Only initialize genuinely new images. Cloned images already carry
            // their own style/model state and should remain exact copies.
            if (!hasExistingSizing) {
              const rawSrc = existingAttributes.src || component.get('src') || '';
              
              let isGrapesJsSvgPlaceholder = false;
              if (typeof rawSrc === 'string' && rawSrc.startsWith('data:image/svg+xml;base64,')) {
                try {
                  const base64Content = rawSrc.split(',')[1] || '';
                  const decoded = atob(base64Content);
                  isGrapesJsSvgPlaceholder = decoded.includes('transform: scale(0.75)') && decoded.includes('M8.5 13.5l2.5');
                } catch (e) {
                  isGrapesJsSvgPlaceholder = false;
                }
              }

              const initialSrc = isGrapesJsSvgPlaceholder || !rawSrc ? imagePlaceholder : rawSrc;
              const isPlaceholder = initialSrc === imagePlaceholder;
              component.addStyle({
                display: 'inline-block',
                verticalAlign: 'top',
                width: isPlaceholder ? '100%' : 'auto',
                maxWidth: 'none',
                float: 'none',
                margin: '0'
              });
              component.set('isNewImageComponent', true);
              component.set('imageWidthMode', 'auto');
              component.set('src', initialSrc);
              component.set('imageSourceUrl', initialSrc);
              component.addAttributes({ 'data-image-width-mode': 'auto', src: initialSrc, 'data-image-source-url': initialSrc });
            }
          }
        }
      });

      if (!editor.__imageDblClickGuardAttached) {
        const bindImageDblClickGuard = () => {
          const canvasBody = editor.Canvas.getBody();
          if (!canvasBody || canvasBody.__imageDblClickGuardAttached) {
            return;
          }

          canvasBody.addEventListener('dblclick', event => {
            const target = event.target;
            if (target && target.tagName === 'IMG') {
              event.preventDefault();
              event.stopPropagation();
              if (editor.Commands.isActive('open-assets')) {
                try {
                  editor.stopCommand('open-assets');
                } catch (err) {
                  // Silently handle stop errors (modal may not be initialized)
                  console.debug('Error stopping open-assets command:', err);
                }
              }
              if (editor.Modal?.isOpen()) {
                editor.Modal.close();
              }
            }
          }, true);

          canvasBody.__imageDblClickGuardAttached = true;
        };

        editor.on('load', bindImageDblClickGuard);
        bindImageDblClickGuard();
        editor.__imageDblClickGuardAttached = true;
      }

      editor.on('update', component => {
        let components = editor.Components.getComponents();
        let startComponents = [];
        let endComponents = [];
        let nestedComponents = [];
        let stack = [];
      
        components.models.forEach(component => {
          if (component.attributes.type === "iteration-component" && component.attributes.item.key) {
            if (component.attributes.item.iterationType === 'startIteration') {
              startComponents.push(component.attributes.item.key);
              stack.push(component.attributes.item.key);
            }
            if (component.attributes.item.iterationType === 'endIteration') {
              endComponents.push(component.attributes.item.key);
              if (stack.length > 0) {
                let startKey = stack.pop();
                let endKey = component.attributes.item.key;
                if (startKey === endKey) {
                  nestedComponents.push({ start: startKey, end: component.attributes.item.key });
                }
              }
            }
          }
        });
        if (!isArrayValuesEqual(startComponents, endComponents) || (!isArrayLengthEqual(startComponents, endComponents, nestedComponents))) {
          setIsValidationError(prevState => ({
            ...prevState,
            ['iterationValidation']: { isDisabled: true }
          }));
        } else {
          setIsValidationError(prevState => {
            delete prevState['iterationValidation'];
            return { ...prevState };
          });
        }
      });

      if (isTemplateReadOnly()) {
        try {
          editor.runCommand('core:preview', {});
        } catch (error) {
          console.log('Error running core:preview command:', error);
        }
        const preview = document.querySelector('.gjs-off-prv');
        if (preview) {
          preview.style.display = 'none';
        }
        const highlight = document.querySelector('.gjs-editor');
        if (highlight) {
          highlight.style.pointerEvents = 'none';
        }
      }  

      const styleManager = editor.StyleManager;

      const setGeneralSectorVisible = isVisible => {
        const generalSector = styleManager.getSector('general');
        const generalSectorEls = document.querySelectorAll(
          '.gjs-sm-sector[data-sector="general"], .gjs-sm-sector.gjs-sm-sector__general'
        );

        if (generalSectorEls.length) {
          generalSectorEls.forEach(el => {
            el.style.display = isVisible ? '' : 'none';
          });
        }

        const fallbackEl = generalSector?.view?.el;
        if (fallbackEl) {
          fallbackEl.style.display = isVisible ? '' : 'none';
        }
      };

      styleManager.addType('image-property', {
        create({ change }) {
          const el = document.createElement('div');
          el.innerHTML = `
            <style>
              .gjs-sm-property[data-property="image-source"] {
                width: 100% !important;
                max-width: 100% !important;
                float: none !important;
                display: block !important;
              }
              .gjs-sm-property[data-property="image-source"] > .gjs-sm-label {
                display: none !important;
                width: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .gjs-sm-property[data-property="image-source"] .gjs-sm-field,
              .gjs-sm-property[data-property="image-source"] .gjs-fields,
              .gjs-sm-property[data-property="image-source"] .gjs-field,
              .gjs-sm-property[data-property="image-source"] .gjs-sm-input,
              .gjs-sm-property[data-property="image-source"] .gjs-sm-input-holder {
                width: 100% !important;
                max-width: 100% !important;
                box-sizing: border-box;
              }
              .gjs-sm-property[data-property="image-source"] .gjs-image-property-wrapper > * {
                width: 100% !important;
                max-width: 100% !important;
                box-sizing: border-box;
              }
              .gjs-sm-property[data-property="image-source"] .gjs-image-property-wrapper .gjs-sm-label {
                display: block !important;
                width: auto !important;
                margin: 0 0 4px 0 !important;
                padding: 0 !important;
              }
              .gjs-sm-property[data-property="image-source"] .gjs-image-property-wrapper .gjs-sm-label-wrp {
                color: inherit !important;
              }
              .gjs-sm-property[data-property="image-source"] .gjs-image-align-select {
                width: 100%;
                box-sizing: border-box;
                background: transparent;
                border: none;
                outline: none;
                color: inherit;
                font-size: 12px;
              }
            </style>
            <div class="gjs-image-property-wrapper" style="display:flex; flex-direction:column; gap:10px; width:100%; padding:4px 0; box-sizing:border-box;">
              <!-- Upload row -->
              <div style="display:flex; gap:6px; width:100%; position:relative;">
                <button
                  type="button"
                  class="gjs-image-upload-btn"
                  style="flex:1; min-height:36px; border:1px solid #c9d1e3; background:#ffffff; color:#3f4d67; border-radius:6px; font-weight:600; cursor:pointer;"
                >
                    ${editorStaticContent?.image_upload_label || `Upload image`}
                </button>
                <div style="position:relative;">
                  <button
                    type="button"
                    class="gjs-more-images-btn"
                    style="min-height:36px; padding:0 12px; border:1px solid #c9d1e3; background:#ffffff; color:#3f4d67; border-radius:6px; font-weight:600; cursor:pointer; white-space:nowrap; display:flex; align-items:center; gap:6px;"
                  >
                    ${editorStaticContent?.more_images || `More Images`}
                    <span class="gjs-more-images-arrow" style="font-size:10px; line-height:1;">&#9660;</span>
                  </button>
                  <!-- Dropdown menu -->
                  <div
                    class="gjs-assets-dropdown"
                    style="display:none; position:absolute; right:0; top:calc(100% + 4px); width:180px; background:#ffffff; border:1px solid #c9d1e3; border-radius:8px; box-shadow:0 4px 16px rgba(0,0,0,0.13); z-index:999; overflow:hidden;"
                  >
                    <button
                      type="button"
                      class="gjs-more-images-option-uploads"
                      style="width:100%; border:none; background:#fff; text-align:left; padding:10px 12px; font-size:12px; color:#3f4d67; cursor:pointer;"
                    >
                      ${editorStaticContent?.uploads || `Uploads`}
                    </button>
                  </div>
                </div>
              </div>
              <div
                class="gjs-image-dropzone"
                style="width:100%; min-height:86px; display:grid; align-items:center; justify-content:center; padding:12px; border:2px dashed #b8bfd1; border-radius:8px; cursor:pointer; text-align:center; color:#4a5568; background:#f8fafc; box-sizing:border-box; transition:all 0.2s ease;"
              >
                  <span>${editorStaticContent?.image_dropzone_label || `Drop a new image here, or click to select files to upload.`}</span>
                  <span style="font-size:10px;">${editorStaticContent?.dataframe_custom_image_note[0] || 'Note: Supports only'} ${acceptTypesLabel} ${editorStaticContent?.dataframe_custom_image_note[1] || 'images upto'} ${acceptLimitLabel} ${editorStaticContent?.dataframe_custom_image_note[2] || 'MB in size.'}</span>
              </div>
              <input type="file" class="gjs-image-file-input" accept="image/*" style="display:none;" />
              <label
                class="gjs-image-url-label"
                style="width:100%; font-size:12px; font-weight:600; color:#5c667d; margin:2px 0 -4px 0;"
              >
                ${editorStaticContent?.image_url_label || `Image URL`}
              </label>
              <input
                type="text"
                class="gjs-image-url-input"
                placeholder="${editorStaticContent?.image_url_label || `Image URL`}"
                style="width:100%; min-height:36px; padding:8px 10px; border:1px solid #c9d1e3; border-radius:6px; box-sizing:border-box;"
              />

              <!-- Width control -->
              <!-- Width control -->
              <div class="gjs-sm-property gjs-sm-composite" style="width:100%; box-sizing:border-box; padding:4px 0 0;">
                <div class="gjs-sm-label" style="display:flex; align-items:center; justify-content:space-between; margin-bottom:5px;">
                  <span class="gjs-sm-label-wrp" style="font-size:12px;">${editorStaticContent?.width || `Width`}</span>
                  <label style="display:flex; align-items:center; gap:5px; cursor:pointer; user-select:none;">
                    <span style="font-size:11px;">Auto</span>
                    <span style="position:relative; display:inline-block; width:32px; height:16px; flex-shrink:0;">
                      <input type="checkbox" class="gjs-image-width-auto" checked style="opacity:0; width:0; height:0; position:absolute;" />
                      <span class="gjs-image-toggle-slider" style="position:absolute; inset:0; background:#777; border-radius:16px; cursor:pointer; transition:background .2s;">
                        <span class="gjs-image-toggle-knob" style="position:absolute; height:10px; width:10px; top:3px; left:19px; background:#fff; border-radius:50%; transition:left .2s; display:block;"></span>
                      </span>
                    </span>
                  </label>
                </div>
                <div class="gjs-image-width-slider-row" style="display:flex; align-items:center; gap:8px; opacity:0.35; pointer-events:none;">
                  <input
                    type="range"
                    class="gjs-image-width-range"
                    min="1" max="100" value="100"
                    style="flex:1; height:4px; accent-color:#777; cursor:pointer; outline:none;"
                  />
                  <span class="gjs-image-width-value" style="font-size:11px; min-width:30px; text-align:right;">100%</span>
                </div>
              </div>

              <!-- Alignment control -->
              <div class="gjs-sm-property" style="width:100%; box-sizing:border-box; padding:4px 0 0;">
                <div class="gjs-sm-label" style="margin-bottom:5px;">
                  <span class="gjs-sm-label-wrp" style="font-size:12px;">${editorStaticContent?.alignment || `Alignment`}</span>
                </div>
                <div class="gjs-field gjs-field-select" style="width:100%; box-sizing:border-box; padding:2px 8px; border-radius:3px;">
                  <select class="gjs-image-align-select">
                    <option value="left">${editorStaticContent?.left_label || `Left`}</option>
                    <option value="center">${editorStaticContent?.center_label || `Center`}</option>
                    <option value="right">${editorStaticContent?.right_label || `Right`}</option>
                    <option value="justify">${editorStaticContent?.justify_label || `Justify`}</option>
                  </select>
                </div>
              </div>

              <!-- Alt text -->
              <div class="gjs-sm-property" style="width:100%; box-sizing:border-box; padding:4px 0 0;">
                <div class="gjs-sm-label" style="margin-bottom:5px;">
                  <span class="gjs-sm-label-wrp" style="font-size:12px;">${editorStaticContent?.alternate_text_label || `Alternate Text`}</span>
                </div>
                <div class="gjs-field gjs-field-text" style="width:100%; box-sizing:border-box; padding:6px 8px; border-radius:3px;">
                  <input
                    type="text"
                    class="gjs-image-alt-input"
                    style="width:100%; box-sizing:border-box; background:transparent; border:none; outline:none; color:inherit; font-size:12px; padding:2px 0;"
                  />
                </div>
              </div>

              <!-- Action URL -->
              <label
                class="gjs-image-action-url-label"
                style="width:100%; font-size:12px; font-weight:600; color:#5c667d; margin:2px 0 -4px 0;"
              >
                ${editorStaticContent?.action_url_label || `Action URL`}
              </label>
              <input
                type="url"
                class="gjs-image-action-url-input"
                data-source="action-url"
                placeholder="https://example.com"
                style="width:100%; min-height:36px; padding:8px 10px; border:1px solid #c9d1e3; border-radius:6px; box-sizing:border-box;"
              />

            </div>
          `;

          const fileInput = el.querySelector('.gjs-image-file-input');
          const uploadButton = el.querySelector('.gjs-image-upload-btn');
          const moreImagesBtn = el.querySelector('.gjs-more-images-btn');
          const assetsDropdown = el.querySelector('.gjs-assets-dropdown');
          const uploadsOption = el.querySelector('.gjs-more-images-option-uploads');
          const dropZone = el.querySelector('.gjs-image-dropzone');
          const urlInput = el.querySelector('.gjs-image-url-input');
          const actionUrlInput = el.querySelector('.gjs-image-action-url-input');

          // Toggle More Images dropdown menu
          if (moreImagesBtn && assetsDropdown) {
            const arrow = moreImagesBtn.querySelector('.gjs-more-images-arrow');
            const closeDropdown = () => {
              assetsDropdown.style.display = 'none';
              if (arrow) arrow.innerHTML = '&#9660;';
            };
            moreImagesBtn.addEventListener('click', event => {
              event.stopPropagation();
              const isOpen = assetsDropdown.style.display !== 'none';
              if (isOpen) {
                closeDropdown();
              } else {
                assetsDropdown.style.display = 'block';
                if (arrow) arrow.innerHTML = '&#9650;';
              }
            });
            if (uploadsOption) {
              uploadsOption.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();
                const selectedComponent = editor.getSelected();
                if (selectedComponent?.get('type') === 'image') {
                  activeImageComponentRef.current = selectedComponent;
                }
                setUploadsOpen(true);
                setKeysOpen(false);
                closeDropdown();
              });
            }
            // Close when clicking outside
            document.addEventListener('click', function onDocClick(e) {
              if (!assetsDropdown.contains(e.target) && e.target !== moreImagesBtn) {
                closeDropdown();
              }
            });
          }

          const emitImageChange = payload => {
            change({
              event: {
                target: {
                  value: payload.value,
                  dataset: {
                    source: payload.source,
                    filename: payload.filename || ''
                  }
                }
              }
            });
          };

          const preventDragDefaults = event => {
            event.preventDefault();
            event.stopPropagation();
          };

          const setDropZoneActive = isActive => {
            dropZone.style.borderColor = isActive ? '#0b5fff' : '#b8bfd1';
            dropZone.style.background = isActive ? '#eef4ff' : '#f8fafc';
            dropZone.style.color = isActive ? '#0b3ea8' : '#4a5568';
          };

          const onFileSelected = async file => {
            if (!file || !String(file.type || '').toLowerCase().startsWith('image/')) {
              return;
            }
            if (acceptTypes.indexOf(file.type) < 0) {
              openSnackBarError(
                'error',
                `${file?.name} ${editorStaticContent?.dataframe_error_image_size[0] || `is not supported`}. ${acceptTypesLabel}`
              );
              return;
            }
            if (file.size > acceptLimit) {
              openSnackBarError(
                'error',
                `${editorStaticContent?.dataframe_error_image_format[0] || `Size of`} ${file?.name} ${editorStaticContent?.dataframe_error_image_format[1] || `is`} ${returnFileSize(file?.size)}. ${editorStaticContent?.dataframe_error_image_format[2] || `Maximum allowed size is`} ${acceptLimitLabel} MB`
              );
              return;
            }
            if (!currentTenantId || !currentBusinessContextId) {
              openSnackBarError('error', `${editorStaticContent?.missing_tenant_business_context || 'Unable to upload: missing tenant or business context.'}`);
              return;
            }
            try {
              const endpoint = getContentServiceUploadApiRoot(currentTenantId, currentBusinessContextId);
              const customTag = getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm';
              const formData = new FormData();
              formData.append('file', file);
              const response = await axios.post(endpoint, formData, {
                params: {
                  customTag: customTag
                }
              });
              const cdnUrl = response?.data?.cdnPublicUrl || response?.data?.publicUrl || response?.data?.filelink || response?.data?.url;
              if (cdnUrl) {
                dispatch({ type: uploadActions.GET_UPLOADS });
                emitImageChange({ value: cdnUrl, source: 'upload', filename: file.name });
              } else {
                openSnackBarError('error', `${editorStaticContent?.cdn_url_not_found || 'Upload successful but CDN URL not found in response.'}`);
              }
            } catch (error) {
              const responseData = error?.response?.data;
               const isNetworkError = !error?.response && error?.message === 'Network Error';
              const isBadRequest = error?.response?.status === 400 || responseData?.status === 400 || responseData?.code === 4001;
              const isDuplicate = error?.response?.status === 409 || responseData?.status === 409 || responseData?.code === 4002;
             const message = isNetworkError
  ? (editorStaticContent?.network_error ||
      'Image upload failed: Network Error. Please try again.')
  : isDuplicate
  ? (editorStaticContent?.image_duplicate_error ||
      'Image upload failed: A file with the same name already exists. Please rename the file and try again.')
  : isBadRequest
  ? (editorStaticContent?.bad_request_error ||
      'Image upload failed: Invalid file format or size. Please check the file and try again.')
  : (editorStaticContent?.image_upload_error ||
      'Image upload failed: Unknown error occurred.');
              openSnackBarError('error', message);
            }
          };

          uploadButton.addEventListener('click', () => fileInput.click());
          dropZone.addEventListener('click', () => fileInput.click());
          fileInput.addEventListener('change', event => {
            const file = event.target?.files?.[0];
            onFileSelected(file);
            event.target.value = '';
          });

          ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, preventDragDefaults);
            el.addEventListener(eventName, preventDragDefaults);
          });

          ['dragenter', 'dragover'].forEach(eventName => {
            dropZone.addEventListener(eventName, event => {
              if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'copy';
              }
              setDropZoneActive(true);
            });
          });

          ['dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, () => setDropZoneActive(false));
          });

          dropZone.addEventListener('drop', event => {
            const file = event.dataTransfer?.files?.[0] || event.dataTransfer?.items?.[0]?.getAsFile?.();
            onFileSelected(file);
          });

          urlInput.addEventListener('change', event => change({ event }));
          actionUrlInput.addEventListener('change', event => change({ event }));

          // Store change callback on element for use in update()
          el.__grapesjsChangeCallback = change;

          // ── Width controls ──────────────────────────────────────────────
          const widthAutoToggle = el.querySelector('.gjs-image-width-auto');
          const widthRange = el.querySelector('.gjs-image-width-range');
          const widthValueLabel = el.querySelector('.gjs-image-width-value');
          const widthSliderRow = el.querySelector('.gjs-image-width-slider-row');
          const toggleKnob = el.querySelector('.gjs-image-toggle-knob');
          const toggleBg = el.querySelector('.gjs-image-toggle-slider');

          const setToggleVisual = checked => {
            if (toggleBg) toggleBg.style.background = checked ? '#777' : '#555';
            if (toggleKnob) toggleKnob.style.left = checked ? '19px' : '3px';
            if (widthSliderRow) widthSliderRow.style.opacity = checked ? '0.35' : '1';
            if (widthSliderRow) widthSliderRow.style.pointerEvents = checked ? 'none' : 'auto';
          };

          widthAutoToggle.addEventListener('change', () => {
            const isAuto = widthAutoToggle.checked;
            setToggleVisual(isAuto);
            change({ event: { target: { value: isAuto ? '__width_auto__on' : '__width_auto__off' } } });
          });

          widthRange.addEventListener('input', () => {
            widthValueLabel.textContent = widthRange.value + '%';
          });
          widthRange.addEventListener('change', () => {
            widthValueLabel.textContent = widthRange.value + '%';
            change({ event: { target: { value: `__width_pct__${widthRange.value}` } } });
          });

          // ── Alignment controls ──────────────────────────────────────────
          const alignSelect = el.querySelector('.gjs-image-align-select');
          if (alignSelect) {
            alignSelect.addEventListener('change', () => {
              change({ event: { target: { value: `__align__${alignSelect.value}` } } });
            });
          }

          // ── Alt text ────────────────────────────────────────────────────
          const altInput = el.querySelector('.gjs-image-alt-input');
          altInput.addEventListener('change', () => {
            change({ event: { target: { value: `__alt__${altInput.value}` } } });
          });

          return el;
        },
        emit(_context, { event }) {
          const selectedComponent = editor.getSelected();
          const type = selectedComponent?.get('type');
          const source = event?.target?.dataset?.source;
          const rawValue = event?.target?.value || '';
          const value = source === 'upload' ? rawValue : rawValue.trim();
          const isActionField = source === 'action-url';

          const refreshImagePropertyPanel = () => {
            setTimeout(() => {
              const imageSectorProps = styleManager.getProperties('imageSector');
              const imageProperty = imageSectorProps?.models?.find(
                prop => prop.get('type') === 'image-property'
              );
              imageProperty?.trigger('change:value');
            }, 0);
          };

          if (type !== 'image' || (!value && !isActionField)) {
            return;
          }

          if (isActionField) {
            const attributes = selectedComponent.getAttributes?.() || {};
            const parentComponent = selectedComponent.parent?.();
            const parentTag = (parentComponent?.get?.('tagName') || '').toLowerCase();
            const parentAttributes = parentComponent?.getAttributes?.() || {};
            const currentActionUrl =
              selectedComponent.get('imageActionUrl') ||
              attributes['data-image-action-url'] ||
              (parentTag === 'a' ? parentAttributes.href || '' : '');
            const currentActionTarget =
              selectedComponent.get('imageActionTarget') ||
              attributes['data-image-action-target'] ||
              (parentTag === 'a' ? parentAttributes.target || '_self' : '_self');

            const nextActionUrl = value;
            const nextActionTarget = currentActionTarget;

            selectedComponent.set('imageActionUrl', nextActionUrl);
            selectedComponent.set('imageActionTarget', nextActionTarget || '_self');
            selectedComponent.addAttributes({
              'data-image-action-url': nextActionUrl,
              'data-image-action-target': nextActionTarget || '_self'
            });

            if (parentTag === 'a' && nextActionUrl) {
              parentComponent.addAttributes({
                href: nextActionUrl,
                target: nextActionTarget || '_self'
              });
              if ((nextActionTarget || '_self') === '_blank') {
                parentComponent.addAttributes({ rel: 'noopener noreferrer' });
              } else {
                const anchorAttrs = parentComponent.getAttributes?.() || {};
                delete anchorAttrs.rel;
                parentComponent.setAttributes(anchorAttrs);
              }
            }

            selectedComponent.trigger('change:attributes');
            refreshImagePropertyPanel();
            return;
          }

          // ── Width: auto toggle ──────────────────────────────────────────
          if (value === '__width_auto__on') {
            selectedComponent.set('imageWidthMode', 'auto');
            selectedComponent.addAttributes({ 'data-image-width-mode': 'auto' });
            selectedComponent.trigger('change:attributes');
            // Defer style + resizable changes so GrapesJS overlay recalc
            // doesn't crash before the canvas element has a valid bounding rect.
            setTimeout(() => {
              selectedComponent.addStyle({
                width: 'auto',
                height: 'auto',
                maxWidth: 'none',
                maxHeight: 'none'
              });
              selectedComponent.set('resizable', false);
              refreshImagePropertyPanel();
            }, 0);
            return;
          }

          if (value === '__width_auto__off') {
            selectedComponent.set('imageWidthMode', 'manual');
            selectedComponent.addAttributes({ 'data-image-width-mode': 'manual' });
            selectedComponent.trigger('change:attributes');
            setTimeout(() => {
              selectedComponent.set('resizable', true);
              refreshImagePropertyPanel();
            }, 0);
            return;
          }

          // ── Width: percentage slider ────────────────────────────────────
          if (value.startsWith('__width_pct__')) {
            const pct = value.replace('__width_pct__', '') + '%';
            selectedComponent.addStyle({ width: pct, maxWidth: '100%' });
            selectedComponent.set('imageWidthMode', 'manual');
            selectedComponent.addAttributes({ 'data-image-width-mode': 'manual' });
            selectedComponent.trigger('change:attributes');
            refreshImagePropertyPanel();
            return;
          }

          // ── Alignment ───────────────────────────────────────────────────
          if (value.startsWith('__align__')) {
            const align = value.replace('__align__', '');
            const styleMap = {
              left:    { display: 'block', margin: '0' },
              center:  { display: 'block', margin: '0 auto' },
              right:   { display: 'block', margin: '0 0 0 auto' },
              justify: { display: 'block', margin: '0' }
            };
            const s = styleMap[align] || styleMap.left;
            const applyStyle = { float: 'none', display: s.display, margin: s.margin };
            selectedComponent.addStyle(applyStyle);
            selectedComponent.set('imageAlignMode', align);
            selectedComponent.addAttributes({ 'data-image-align': align });
            selectedComponent.trigger('change:attributes');

            refreshImagePropertyPanel();
            return;
          }

          // ── Alt text ────────────────────────────────────────────────────
          if (value.startsWith('__alt__')) {
            const altText = value.replace('__alt__', '');
            selectedComponent.addAttributes({ alt: altText });
            selectedComponent.trigger('change:attributes');
            return;
          }

          // ── Image src (upload / URL) ────────────────────────────────────
          // If the image is currently the placeholder, reset its width limit so it can follow its own dimensions
          const isNew = selectedComponent.get('isNewImageComponent');
          if (isNew) {
            selectedComponent.addStyle({
              height: 'auto',
              maxHeight: 'none',
              width: 'auto'
            });
            selectedComponent.set('isNewImageComponent', false);
          } else {
            selectedComponent.addStyle({
              height: 'auto',
              maxHeight: 'none'
            });
          }

          // Keep GrapesJS model src and DOM attributes in sync so export/save
          // always serializes the latest URL instead of a stale placeholder.
          selectedComponent.set('src', value);
          selectedComponent.set('imageSourceUrl', value);
          selectedComponent.addAttributes({
            src: value,
            'data-image-source-url': value,
            'data-image-source': source === 'upload' ? 'upload' : 'url'
          });
          selectedComponent.trigger('change:attributes');

          if (source === 'upload') {
            editor.AssetManager.add({
              src: value,
              type: 'image',
              name: event?.target?.dataset?.filename || 'uploaded-image'
            });
          }
        },
        update({ el }) {
          const selectedComponent = editor.getSelected();
          if (selectedComponent?.get('type') === 'image') {
            activeImageComponentRef.current = selectedComponent;
          }
          const urlInput = el.querySelector('.gjs-image-url-input');
          const actionUrlInput = el.querySelector('.gjs-image-action-url-input');
          if (!urlInput) {
            return;
          }

          if (selectedComponent?.get('type') === 'image') {
            const attributes = selectedComponent.getAttributes?.() || {};
            const isNew = selectedComponent.get('isNewImageComponent');
            
            let currentSrc;
            if (isNew) {
              const modelSrc = selectedComponent.get('imageSourceUrl') || '';
              const attributeSecUrl = attributes['data-image-source-url'] || '';
              const attributeSrc = attributes.src || '';
              const modelCurrentSrc = selectedComponent.get('src') || '';
              const isGrapesJsSvgPlaceholder = src => {
                if (typeof src !== 'string' || !src.startsWith('data:image/svg+xml;base64,')) {
                  return false;
                }
                try {
                  const base64Content = src.split(',')[1] || '';
                  const decoded = atob(base64Content);
                  return decoded.includes('transform: scale(0.75)') && decoded.includes('M8.5 13.5l2.5');
                } catch (e) {
                  return false;
                }
              };
              
              currentSrc = modelSrc || attributeSecUrl;
              if (!currentSrc) {
                if (isGrapesJsSvgPlaceholder(attributeSrc) || isGrapesJsSvgPlaceholder(modelCurrentSrc)) {
                  currentSrc = imagePlaceholder;
                } else {
                  currentSrc = attributeSrc || modelCurrentSrc || '';
                }
              }
            } else {
              currentSrc = selectedComponent.get('src') || attributes.src || '';
            }
            const parentComponent = selectedComponent.parent?.();
            const parentTag = (parentComponent?.get?.('tagName') || '').toLowerCase();
            const parentAttributes = parentComponent?.getAttributes?.() || {};
            const imageEl = selectedComponent.getEl?.();
            const parentAnchor = imageEl?.closest?.('a');
            const currentActionUrl =
              selectedComponent.get('imageActionUrl') ||
              attributes['data-image-action-url'] ||
              (parentTag === 'a' ? parentAttributes.href || '' : '') ||
              parentAnchor?.getAttribute?.('href') ||
              '';
            const currentActionTarget =
              selectedComponent.get('imageActionTarget') ||
              attributes['data-image-action-target'] ||
              (parentTag === 'a' ? parentAttributes.target || '_self' : '_self') ||
              parentAnchor?.getAttribute?.('target') ||
              '_self';

            // Preserve existing image URLs (including base64) exactly as-is.
            urlInput.value = currentSrc;

            if (actionUrlInput) {
              actionUrlInput.value = currentActionUrl;
            }

            // ── Sync width controls ───────────────────────────────────────
            const widthAutoToggle = el.querySelector('.gjs-image-width-auto');
            const widthRange = el.querySelector('.gjs-image-width-range');
            const widthValueLabel = el.querySelector('.gjs-image-width-value');
            const widthSliderRow = el.querySelector('.gjs-image-width-slider-row');
            const toggleKnob = el.querySelector('.gjs-image-toggle-knob');
            const toggleBg = el.querySelector('.gjs-image-toggle-slider');

            if (widthAutoToggle) {
              const styles = selectedComponent.getStyle?.() || {};
              const widthMode = selectedComponent.get('imageWidthMode') || attributes['data-image-width-mode'];
              const widthStyle = styles.width || '';
              const isAuto = widthMode ? widthMode === 'auto' : true;
              widthAutoToggle.checked = isAuto;
              if (toggleBg) toggleBg.style.background = isAuto ? '#777' : '#555';
              if (toggleKnob) toggleKnob.style.left = isAuto ? '19px' : '3px';
              if (widthSliderRow) widthSliderRow.style.opacity = isAuto ? '0.35' : '1';
              if (widthSliderRow) widthSliderRow.style.pointerEvents = isAuto ? 'none' : 'auto';

              // Show existing specified/rendered width in slider for both auto/manual modes.
              if (widthRange && widthValueLabel) {
                const pctMatch = widthStyle.match(/^(\d+)%$/);
                const pxMatch = widthStyle.match(/^(\d+(?:\.\d+)?)px$/);
                let pctValue = '100';

                if (pctMatch) {
                  pctValue = pctMatch[1];
                } else if (pxMatch) {
                  const imageEl = selectedComponent.getEl?.();
                  const parentWidth = imageEl?.parentElement?.getBoundingClientRect?.().width || 0;
                  if (parentWidth > 0) {
                    pctValue = String(Math.round((Number(pxMatch[1]) / parentWidth) * 100));
                  }
                } else {
                  if (isAuto) {
                    pctValue = getImageAutoWidthPercent(selectedComponent);
                  } else {
                    const imageEl = selectedComponent.getEl?.();
                    const imageWidth = imageEl?.getBoundingClientRect?.().width || 0;
                    const parentWidth = imageEl?.parentElement?.getBoundingClientRect?.().width || 0;
                    if (imageWidth > 0 && parentWidth > 0) {
                      pctValue = String(Math.round((imageWidth / parentWidth) * 100));
                    }
                  }
                }

                const clampedPct = String(Math.min(100, Math.max(1, Number(pctValue) || 100)));
                pctValue = clampedPct;
                widthRange.value = pctValue;
                widthValueLabel.textContent = pctValue + '%';
              }
            }

            // ── Sync alignment controls ───────────────────────────────────
            const alignSelect = el.querySelector('.gjs-image-align-select');
            if (alignSelect) {
              const styles = selectedComponent.getStyle?.() || {};
              const floatVal = styles.float || '';
              const displayVal = styles.display || '';
              const alignMode = selectedComponent.get('imageAlignMode') || attributes['data-image-align'];
              let activeAlign = alignMode || '';
              if (!activeAlign) {
                if (floatVal === 'left') activeAlign = 'left';
                else if (floatVal === 'right') activeAlign = 'right';
                else if (displayVal === 'block' || floatVal === 'none') activeAlign = 'center';
              }
              alignSelect.value = activeAlign || 'center';
            }

            // ── Sync alt text ─────────────────────────────────────────────
            const altInput = el.querySelector('.gjs-image-alt-input');
            if (altInput) {
              altInput.value = attributes.alt || '';
            }
          } else {
            urlInput.value = DEFAULT_IMAGE_PLACEHOLDER_URL;
          }

          // uploads selection is handled by UploadsPanel popup
        },
        destroy() {}
      });

      // Store reference to previous image component to clean up listeners
      let previousImageComponent = null;
      let imageStyleChangeListener = null;
      let imageResizeMonitorId = null;
      let lastObservedImageMetrics = null;

      const getImageMetricsSignature = imageComponent => {
        const imageEl = imageComponent?.getEl?.();
        const currentStyle = imageComponent?.getStyle?.() || {};
        const styleWidth = (currentStyle.width || '').toString().trim();
        const renderedWidth = Math.round(imageEl?.getBoundingClientRect?.().width || 0);
        return `${styleWidth}|${renderedWidth}`;
      };

      const stopImageResizeMonitor = () => {
        if (imageResizeMonitorId !== null) {
          cancelAnimationFrame(imageResizeMonitorId);
          imageResizeMonitorId = null;
        }
        lastObservedImageMetrics = null;
      };

      const startImageResizeMonitor = imageComponent => {
        stopImageResizeMonitor();
        lastObservedImageMetrics = getImageMetricsSignature(imageComponent);

        const monitor = () => {
          const selectedComponent = editor.getSelected();
          if (!selectedComponent || selectedComponent !== imageComponent || selectedComponent.get('type') !== 'image') {
            stopImageResizeMonitor();
            return;
          }

          const currentMetrics = getImageMetricsSignature(imageComponent);
          if (currentMetrics !== lastObservedImageMetrics) {
            lastObservedImageMetrics = currentMetrics;
            syncImageResizeWithPanel(imageComponent);
          }

          imageResizeMonitorId = requestAnimationFrame(monitor);
        };

        imageResizeMonitorId = requestAnimationFrame(monitor);
      };

      const triggerImagePanelUpdate = () => {
        let imageSectorProps = styleManager.getProperties('imageSector');
        let imageProperty = imageSectorProps?.models?.filter(
          prop => prop.get('type') === 'image-property'
        )[0];
        imageProperty?.trigger('change:value');
      };

      const getImageAutoWidthPercent = imageComponent => {
        const imageEl = imageComponent?.getEl?.();
        const parentWidth = imageEl?.parentElement?.getBoundingClientRect?.().width || 0;

        if (parentWidth <= 0) {
          return '100';
        }

        const intrinsicWidth = imageEl?.naturalWidth || 0;
        if (intrinsicWidth > 0) {
          return String(Math.min(100, Math.max(1, Math.round((intrinsicWidth / parentWidth) * 100))));
        }

        const renderedWidth = imageEl?.getBoundingClientRect?.().width || 0;
        if (renderedWidth > 0) {
          return String(Math.min(100, Math.max(1, Math.round((renderedWidth / parentWidth) * 100))));
        }

        return '100';
      };

      const syncImageResizeWithPanel = imageComponent => {
        if (!imageComponent || imageComponent.get('type') !== 'image') {
          return;
        }

        const currentStyle = imageComponent.getStyle?.() || {};
        const currentWidth = (currentStyle.width || '').toString().trim();
        const currentMode = imageComponent.get('imageWidthMode') || imageComponent.getAttributes?.()['data-image-width-mode'];

        // Any non-auto width coming from canvas resize should switch width mode to manual.
        if (currentWidth && currentWidth !== 'auto') {
          if (currentMode === 'auto' || !currentMode) {
            imageComponent.set('imageWidthMode', 'manual');
            imageComponent.addAttributes({ 'data-image-width-mode': 'manual' });
          }
        }

        triggerImagePanelUpdate();
      };

      editor.on('component:styleUpdate', component => {
        const selectedComponent = editor.getSelected();
        if (!selectedComponent || selectedComponent !== component) {
          return;
        }

        if (selectedComponent.get('type') !== 'image') {
          return;
        }

        // Resize handles can update style through editor-level events.
        syncImageResizeWithPanel(selectedComponent);
      });

      editor.on('component:selected', selComponent => {
        let type = selComponent.get('type');
        
        // Remove listener from previous image component if it exists
        if (previousImageComponent && imageStyleChangeListener) {
          previousImageComponent.off('change:style', imageStyleChangeListener);
        }
        stopImageResizeMonitor();
        previousImageComponent = null;
        imageStyleChangeListener = null;

        if (type === 'image') {
          // Defer resizable so the canvas element is laid out before GrapesJS
          // tries to recompute the selection overlay bounding rect.
          const currentMode = selComponent.get('imageWidthMode') || selComponent.getAttributes?.()['data-image-width-mode'];
          setTimeout(() => {
            selComponent.set('resizable', currentMode !== 'auto');
          }, 0);
          setGeneralSectorVisible(true);
          styleManager.removeSector('imageSector');
          styleManager.addSector(
            'imageSector',
            {
              name: 'Image',
              properties: [
                {
                  type: 'image-property',
                  property: 'image-source',
                  label: ' ',
                  full: true
                }
              ]
            },
            { at: 0 }
          );
          let imageSector = styleManager.getSector('imageSector');
          imageSector.set('open', true);

          setTimeout(() => {
            triggerImagePanelUpdate();
          }, 50);

          // Add listener for real-time style changes (for manual resize/drag on canvas)
          previousImageComponent = selComponent;
          imageStyleChangeListener = () => syncImageResizeWithPanel(selComponent);
          selComponent.on('change:style', imageStyleChangeListener);
          startImageResizeMonitor(selComponent);
        } else {
          setGeneralSectorVisible(true);
          styleManager.removeSector('imageSector');
        }
      });

      // Removing the sectors which are having bugs in grapejs
      // styleManager.removeSector("typography");

      // Removing one of the properties from typography sector due to lack of support in print-service
      styleManager.removeProperty("typography",'font-family');

      isHydratingEditorContent = true;
      editor.setComponents(thisTemplate?.jsonContent?.html);
      
      // Restore image action data from saved HTML/link wrappers to component model.
      setTimeout(() => {
        try {
          const allComponents = editor.Components.getComponents();
          const traverseComponents = components => {
            components?.forEach(component => {
              if (component.get('type') === 'image') {
                const attributes = component.getAttributes?.() || {};
                const parentComponent = component.parent?.();
                const parentTag = (parentComponent?.get?.('tagName') || '').toLowerCase();
                const parentAttributes = parentComponent?.getAttributes?.() || {};
                const imageEl = component.getEl?.();
                const parentAnchor = imageEl?.closest?.('a');
                const actionUrl =
                  attributes['data-image-action-url'] ||
                  (parentTag === 'a' ? parentAttributes.href || '' : '') ||
                  parentAnchor?.getAttribute?.('href') ||
                  '';
                const actionTarget =
                  attributes['data-image-action-target'] ||
                  (parentTag === 'a' ? parentAttributes.target || '_self' : '_self') ||
                  parentAnchor?.getAttribute?.('target') ||
                  '_self';

                component.set('imageActionUrl', actionUrl);
                component.set('imageActionTarget', actionTarget || '_self');
              }

              const children = component.components?.models || [];
              if (children.length > 0) {
                traverseComponents(children);
              }
            });
          };

          traverseComponents(allComponents.models || []);
        } catch (err) {
          console.error('Error restoring image action data:', err);
        }
      }, 100);
      
      isHydratingEditorContent = false;
      setGrapesJsBodyId(editor, thisTemplate?.jsonContent?.html);
      editor.setStyle(thisTemplate?.jsonContent?.css);
      const bm = editor.BlockManager;
      const blocks = bm.getAll();
      const toRemove = blocks.filter(block => block.get('category') === 'Forms');
      toRemove.forEach(block => bm.remove(block.get('id')));
    }
  }, [editor]);

  useImperativeHandle(ref, () => ({
    getEditorData() {
      return {
        html: editor.getHtml(),
        css: editor.getCss()
      }
    },
    exportAndSaveTemplate(event) {
      // Sync image action data from component model to DOM attributes before export
      try {
        const allComponents = editor.Components.getComponents();
        const traverseComponents = (components) => {
          components.forEach(component => {
            if (component.get('type') === 'image') {
              const actionUrl = component.get('imageActionUrl') || '';
              const actionTarget = component.get('imageActionTarget') || '_self';
              
              const attributes = {
                'data-image-action-url': actionUrl,
                'data-image-action-target': actionTarget
              };
              component.addAttributes(attributes);
            }
            
            // Traverse child components
            const children = component.components?.models || [];
            if (children.length > 0) {
              traverseComponents(children);
            }
          });
        };
        
        traverseComponents(allComponents.models || []);
      } catch (err) {
        console.error('Error syncing image action data:', err);
      }

      const data = exportGrapeJsContent(editor.getHtml(), editor.getCss());
      pendingSavedContentRef.current = data;
      saveHydrationGuardUntilRef.current = Date.now() + 20000;
      if (saveHydrationGuardTimerRef.current) {
        clearTimeout(saveHydrationGuardTimerRef.current);
      }
      saveHydrationGuardTimerRef.current = setTimeout(() => {
        pendingSavedContentRef.current = null;
        saveHydrationGuardUntilRef.current = 0;
        saveHydrationGuardTimerRef.current = null;
      }, 20000);

      handleChange([
        {
          target: {
            name: 'includesSurveyLink',
            value: isSurveyLinkIncluded(data)
          }
        },
        {
          target: {
            name: 'jsonContent',
            value: data
          }
        },
        {
          target: {
            name: 'content',
            value: data.html
          }
        }
      ]);
      setTriggerSave(true);
      // After saving, focus on the body of the editor instead of the exisiting element which was in focus while clicking the save button 
      // This is to prevent old data from being displayed in property panel
      if (editor) {
        setTimeout(() => {
          try {
            // Get the body/wrapper component and select it
            const wrapper = editor.getWrapper();
            if (wrapper) {
              editor.select(wrapper);
            } else {
              // Fallback: clear selection
              editor.select();
            }
          } catch (error) {
            console.log('Error focusing on editor:', error);
          }
        }, 100);
      }
    }
  }));

  const isSurveyLinkIncluded = data =>
    data.html.includes('/{{surveyRecipientIdentifier}}');

  const handleSnackBarClose = () => {
    setSnackBarInfo({ show: false });
  };

  const openSnackBarError = (severity, message) => {
    setSnackBarInfo({
      show: true,
      severity,
      message
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    setWindowWidth(window.innerWidth);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (props.templateKeys.length > 0) {
      let temp = filterDataFrame(props.templateKeys, thisTemplate);
      setDataFrameKeys({ ...temp });
    }
  }, [props.templateKeys, thisTemplate.eventTypePath]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const handleComponentSelected = () => {
      if (uploadsOpen) {
        setUploadsOpen(false);
      }
    };

    editor.on('component:selected', handleComponentSelected);
    return () => {
      editor.off('component:selected', handleComponentSelected);
    };
  }, [editor, uploadsOpen]);

  // Only trigger save when triggerSave state changes to true.
  // We don't include saveTemplate in dependencies because including it would cause
  // this effect to re-run on every parent render (since saveTemplate is recreated),
  // leading to infinite save loops and 409 conflict errors on the backend.
  useEffect(() => {
    if (triggerSave) {
      shouldAutoSave ? autoSaveTemplate() : saveTemplate();
      setTriggerSave(false);
    }
  }, [triggerSave]);

  useEffect(() => {
    if (editor && !triggerSave) {
      const incomingContent = thisTemplate?.jsonContent;
      const pendingSavedContent = pendingSavedContentRef.current;
      const isWithinSaveGuardWindow = Date.now() < saveHydrationGuardUntilRef.current;

      if (pendingSavedContent && isWithinSaveGuardWindow) {
        const isLatestSavedSnapshot =
          incomingContent?.html === pendingSavedContent?.html &&
          incomingContent?.css === pendingSavedContent?.css;

        // Ignore stale snapshots arriving after save (race with async store/API updates).
        if (!isLatestSavedSnapshot) {
          return;
        }

        // Editor is already on this content; do not rehydrate and reset selection/panel state.
        return;
      }

      // Guard window expired; resume normal hydration behavior.
      if (!isWithinSaveGuardWindow && pendingSavedContentRef.current) {
        pendingSavedContentRef.current = null;
      }

      editor.setComponents(thisTemplate?.jsonContent?.html);
      
      // Restore image action data from saved HTML/link wrappers to component model.
      setTimeout(() => {
        try {
          const allComponents = editor.Components.getComponents();
          const traverseComponents = components => {
            components?.forEach(component => {
              if (component.get('type') === 'image') {
                const attributes = component.getAttributes?.() || {};
                const parentComponent = component.parent?.();
                const parentTag = (parentComponent?.get?.('tagName') || '').toLowerCase();
                const parentAttributes = parentComponent?.getAttributes?.() || {};
                const imageEl = component.getEl?.();
                const parentAnchor = imageEl?.closest?.('a');
                const actionUrl =
                  attributes['data-image-action-url'] ||
                  (parentTag === 'a' ? parentAttributes.href || '' : '') ||
                  parentAnchor?.getAttribute?.('href') ||
                  '';
                const actionTarget =
                  attributes['data-image-action-target'] ||
                  (parentTag === 'a' ? parentAttributes.target || '_self' : '_self') ||
                  parentAnchor?.getAttribute?.('target') ||
                  '_self';

                component.set('imageActionUrl', actionUrl);
                component.set('imageActionTarget', actionTarget || '_self');
              }

              const children = component.components?.models || [];
              if (children.length > 0) {
                traverseComponents(children);
              }
            });
          };

          traverseComponents(allComponents.models || []);
        } catch (err) {
          console.error('Error restoring image action data:', err);
        }
      }, 100);
      
      setGrapesJsBodyId(editor, thisTemplate?.jsonContent?.html);    
    }
  }, [thisTemplate.jsonContent, triggerSave]);

  useEffect(() => {
    return () => {
      if (saveHydrationGuardTimerRef.current) {
        clearTimeout(saveHydrationGuardTimerRef.current);
      }
      saveHydrationGuardUntilRef.current = 0;
    };
  }, []);

  useEffect(() => {
    if (templateLanguageContent) {
      setEditorStaticContent(getTemplateLanguageContent("grapesjsEditor"));
    }
  }, [templateLanguageContent]);

  const isTemplateReadOnly = () => {
    return (
      thisTemplate.id &&
      (currentTenantId !== thisTemplate.tenantId ||
        currentBusinessContextId !== thisTemplate.businessContextId)
    ) || isReadOnlyMode();
  };

  return (
    <Grid container spacing={3}>
      {!isTemplateReadOnly() && (
        <Grid container className={classes.editorBar} ref={emailWrapperRef}>
          <Box className={classes.subjectLine}>{children}</Box>
          <Box style={{ width: '30%' }} className={classes.tools}>
            <AppBar
              position="static"
              className={classes.appBar}
              color="default"
              elevation={0}>
              <Toolbar className={classes.toolbar}>
                <IconButton
                  onClick={() => {
                    setKeysOpen(!keysOpen);
                    setUploadsOpen(false);
                  }}
                  edge="start"
                  classes={{ label: classes.iconButtonLabel }}
                  color="inherit"
                  aria-label="menu"
                  label="Template Keys">
                  <Code />
                  <Typography variant="button" display="block" gutterBottom>
                    {editorStaticContent?.templateKeys || `Template Keys`}
                  </Typography>
                </IconButton>
              </Toolbar>
            </AppBar>
          </Box>
        </Grid>
      )}
      <Grid item xs={12} className={classes.editorWrapper}>
        {keysOpen && (
          <div
            className={classes.toolContainer}
            style={{
              width: '30%'
            }}>
            <Collapse
              in={keysOpen}
              style={{
                width: '100%',
                float: 'right'
              }}>
              <Box elevation={4} className={classes.templateKeysPanel}>
                <TemplateKeyBank
                  isOpen={keysOpen}
                  templateKeys={templateKeys()}
                  dateFormatOptions={dateFormatMenuOptions}
                  customBoxStyle={{ height: '95%', maxHeight: 'none' }}
                  customSearchStyle={{ height: '3%' }}
                />
              </Box>
            </Collapse>
          </div>
        )}
        {uploadsOpen && (
          <div
            className={classes.toolContainer}
            style={{
              width: '30%'
            }}>
            <Collapse
              in={uploadsOpen}
              style={{
                width: '100%',
                float: 'right'
              }}>
              <Box elevation={4} className={classes.templateKeysPanel}>
                <UploadsPanel
                  isOpen={uploadsOpen}
                  uploads={uploadAssets}
                  onUploadFiles={handleUploadsPanelFiles}
                  uploadInProgress={isUploadingAssets}
                  panelMode="builder"
                  showHeader
                  showTopUploadButton
                  onClose={() => setUploadsOpen(false)}
                  onSelectImage={(asset, imageUrl) => {
                    const url = imageUrl || getAssetImageLocation(asset);
                    applySelectedAssetImage(url);
                  }}
                  onDeleteImage={handleDeleteUpload}
                  translations={editorStaticContent}
                />
              </Box>
            </Collapse>
          </div>
        )}
        <div id="gjs" ref={emailEditorRef}></div>
      </Grid>
      <Snackbar
        open={snackbarInfo.show}
        onClose={handleSnackBarClose}
        autoHideDuration={3000}>
        <Alert show={snackbarInfo.show} severity={snackbarInfo.severity}>
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
});

export default TemplateEditor;
